import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Users, Copy, Share2, Gift, Crown, Sparkles, Link2, Trophy, Check,
  QrCode, ChevronRight, TrendingUp, ArrowDownToLine, ArrowUpFromLine, Coins,
  X, MessageCircle, Send, Facebook, Twitter, Mail,
  Edit3, Loader2, AlertCircle, Download, ImageDown, FileText,
  Search, ChevronDown, ChevronUp, Filter,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import { api } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { useAuth } from "../../contexts/AuthContext";

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
}

export default function MyReferralPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(""); // track which button showed "Copied!"
  const [ngr, setNgr] = useState(null);
  const [ngrHistory, setNgrHistory] = useState([]);
  const [shareInfo, setShareInfo] = useState(null); // {url, title, text} | null

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/referral");
        setData(data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
      // Fire NGR fetches in parallel — non-blocking
      try {
        const [{ data: ovw }, { data: hist }] = await Promise.all([
          api.get("/referral-ngr/overview"),
          api.get("/referral-ngr/history?limit=40"),
        ]);
        setNgr(ovw);
        setNgrHistory(hist?.items || []);
      } catch {
        /* NGR endpoints optional — keep UI working */
      }
    })();
  }, []);

  const copy = async (text, key, label) => {
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success(`${label} copied`);
      setCopied(key);
      setTimeout(() => setCopied(""), 1400);
    } else {
      toast.error("Could not copy");
    }
  };

  const onShare = async (url, title, text) => {
    if (!url) return;
    // 1) Web Share API (mobile + a few desktop browsers, requires HTTPS).
    //    Only count it as "handled" if the user actually shared — when the
    //    user cancels, navigator.share throws AbortError; we fall through to
    //    the custom modal in that case so the button never feels broken.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url });
        return; // shared OK
      } catch (e) {
        // AbortError → user cancelled native sheet, do nothing (don't fall through)
        if (e && (e.name === "AbortError" || /aborted|cancel/i.test(e.message || ""))) {
          return;
        }
        // Any other failure (NotAllowedError, permission, no targets, etc.) → fall through to modal
      }
    }
    // 2) Fallback: open our custom share modal with platform buttons
    setShareInfo({ url, title: title || "Maxlive88 invite", text: text || "" });
  };

  const symbol = data?.currency === "BDT" ? "৳" : "$";
  const isAff = !!data?.is_affiliate;
  const ngrPct = Number(ngr?.commission_pct ?? data?.commission_pct ?? 10);
  const lossPaid = Number(data?.loss_commission_paid || 0); // legacy reference only
  const ngrTotal = Number(ngr?.stats?.total_earned || 0);
  const ngrToday = Number(ngr?.stats?.today_earned || 0);
  const ngrYday  = Number(ngr?.stats?.yesterday_earned || 0);

  // Compute 7-day / 15-day / this-month NGR aggregates from history items.
  // Each item is expected to have { date | created_at, amount }.
  const ngrPeriods = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start7  = new Date(today); start7.setDate(today.getDate() - 6);   // last 7 days inclusive
    const start15 = new Date(today); start15.setDate(today.getDate() - 14); // last 15 days inclusive
    const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    let s7 = 0, s15 = 0, sMonth = 0;
    for (const it of (ngrHistory || [])) {
      const amt = Number(it.amount || it.earned || it.commission || 0);
      if (!amt) continue;
      const raw = it.date || it.day || it.created_at;
      if (!raw) continue;
      const d = new Date(raw);
      if (isNaN(d.getTime())) continue;
      d.setHours(0, 0, 0, 0);
      if (d >= start7)  s7  += amt;
      if (d >= start15) s15 += amt;
      if (d >= startMonth) sMonth += amt;
    }
    return { d7: s7, d15: s15, mtd: sMonth };
  })();
  const totalEarnings = ngrTotal + lossPaid + Number(data?.affiliate_paid_commission || 0) + Number(data?.affiliate_cpa_paid || 0);

  return (
    <ProfileShell title="My referral">
      <div className="py-2 pb-16 space-y-4" data-testid="referral-page">
        {loading && (
          <div className="text-center text-white/45 text-sm py-10">Loading...</div>
        )}

        {!loading && data && (
          <>
            {/* ============== Hero ============== */}
            <div
              className="relative rounded-3xl p-6 overflow-hidden"
              style={{
                background:
                  "radial-gradient(at 20% 20%, rgba(255,201,51,0.22) 0%, transparent 40%), radial-gradient(at 80% 80%, rgba(25,165,92,0.25) 0%, transparent 45%), linear-gradient(135deg,#0c1420 0%,#0a1119 70%)",
                border: "1px solid rgba(255,201,51,0.18)",
              }}
            >
              {/* Decorative coins */}
              <div aria-hidden className="absolute -right-10 -top-8 text-[130px] leading-none opacity-[0.12] select-none font-black text-[#ffc933]">৳</div>
              <div aria-hidden className="absolute -left-6 -bottom-10 text-[90px] leading-none opacity-[0.08] select-none font-black text-[#21c46e]">৳</div>

              <div className="relative flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-[#ffc933]/15 text-[#ffc933] border border-[#ffc933]/30">
                  <Sparkles size={11} /> Invite & Earn
                </span>
                {isAff && (
                  <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-purple-500/15 text-purple-300 border border-purple-500/30">
                    <Crown size={11} /> Affiliate Partner
                  </span>
                )}
              </div>

              <h2 className="relative text-white font-black text-[22px] leading-tight mt-3">
                Invite friends,<br /> earn <span className="text-[#ffc933]">{ngrPct.toFixed(0)}% of their daily NGR</span>
              </h2>
              <p className="relative text-white/60 text-[13px] mt-2 max-w-[34ch] leading-relaxed">
                Share your code. When your friends sign up and play, you earn {ngrPct.toFixed(0)}% of their daily Net Gaming Revenue — credited automatically every day at 12:00 AM BD (রাত ১২টা).
              </p>

              <div className="relative grid grid-cols-3 gap-2 mt-5">
                <TinyStat label="Invited" value={data.invited_count} icon={Users} color="#3b82f6" />
                <TinyStat
                  label="Earned"
                  value={`${symbol}${Number(totalEarnings).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  icon={Trophy}
                  color="#21c46e"
                />
                <TinyStat
                  label="Pending"
                  value={`${symbol}${Number(
                    data?.current_period?.pending_commission ||
                      ngr?.stats?.pending_today ||
                      ngr?.pending ||
                      0
                  ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                  icon={Coins}
                  color="#ffc933"
                />
              </div>
            </div>

            {/* ============== NGR Commission card ============== */}
            <div
              className="rounded-2xl p-4"
              style={{
                background: "linear-gradient(135deg,#0d2418 0%,#0b121c 100%)",
                border: "1px solid rgba(34,197,94,0.30)",
              }}
              data-testid="ngr-commission-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-300 font-bold">
                  <Trophy size={12} /> NGR commission · {ngrPct.toFixed(0)}%
                </div>
                <div className="flex items-center gap-1.5">
                  <NgrOverviewPdfButton />
                  <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[1.5px] font-bold text-emerald-200/80 bg-emerald-500/10 border border-emerald-400/25 rounded-full px-2 py-0.5">
                    <span className="h-1 w-1 rounded-full bg-emerald-400 animate-pulse" /> Daily 12:00 AM BD
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <EarningStat
                  label="Today"
                  value={`${symbol}${ngrToday.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                />
                <EarningStat
                  label="Yesterday"
                  value={`${symbol}${ngrYday.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                />
                <EarningStat
                  label="7 Days"
                  value={`${symbol}${ngrPeriods.d7.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                />
                <EarningStat
                  label="15 Days"
                  value={`${symbol}${ngrPeriods.d15.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                />
                <EarningStat
                  label="This Month"
                  value={`${symbol}${ngrPeriods.mtd.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                />
                <EarningStat
                  label="Lifetime"
                  value={`${symbol}${ngrTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                />
              </div>
              {/* 7-day sparkline */}
              {Array.isArray(ngr?.chart_last7) && ngr.chart_last7.length > 0 && (
                <Last7Spark data={ngr.chart_last7} symbol={symbol} />
              )}
              <div className="mt-3 text-[11px] text-white/45 leading-relaxed">
                NGR = Total Bets − Total Wins − Bonuses − Cashback. Commission is paid only when daily NGR is positive — credited straight to your main wallet.
              </div>
            </div>

            {/* ============== Commission History ============== */}
            {ngrHistory.length > 0 && (
              <div className="rounded-2xl bg-[#0f1622] border border-white/5 overflow-hidden" data-testid="ngr-history-card">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold">
                    <Coins size={12} /> Commission history
                  </div>
                  <div className="text-[11px] text-white/35">{ngrHistory.length} recent</div>
                </div>
                <ul className="divide-y divide-white/5">
                  {ngrHistory.slice(0, 10).map((h, i) => (
                    <li key={h.id || i} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`ngr-history-${i}`}>
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-white truncate">
                          From <span className="text-amber-300">@{h.referee_username || "user"}</span>
                        </div>
                        <div className="text-[10.5px] text-white/40 mt-0.5">
                          NGR {symbol}{Number(h.ngr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} · {h.settlement_date}
                        </div>
                      </div>
                      <div className="font-mono font-extrabold text-emerald-300 text-[14px] flex-shrink-0">
                        +{symbol}{Number(h.commission || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ============== Referral Code card ============== */}
            <CodeCard
              title="Your Referral Code"
              subtitle="Share with friends — perfect for personal invites"
              code={data.code}
              shareUrl={data.share_url}
              accent="#21c46e"
              accentSoft="rgba(25,165,92,0.14)"
              accentBorder="rgba(25,165,92,0.5)"
              icon={Users}
              shareText={`Sign up on Maxlive88 with my code ${data.code} and get a 100% welcome bonus!`}
              copyKey="ref"
              copied={copied}
              onCopyCode={() => copy(data.code, "ref-code", "Code")}
              onCopyLink={() => copy(data.share_url, "ref-link", "Link")}
              onShare={() =>
                onShare(data.share_url, "Join me on Maxlive88",
                  `Sign up with my code ${data.code} — 100% welcome bonus waiting for you.`)
              }
              testidPrefix="referral"
            />

            {/* ============== Custom Refer Code Generator (user-to-user only) ============== */}
            <CustomReferCodeCard
              currentCode={data.code}
              shareBase={data.share_base}
              initialCooldown={data.refer_code_cooldown}
              username={user?.username || user?.full_name || ""}
              shareBonusText={data.share_bonus_text || ""}
              onUpdated={(newCode, newUrl, newCooldown) => {
                setData((prev) => prev ? {
                  ...prev,
                  code: newCode,
                  referral_code: newCode,
                  share_url: newUrl,
                  refer_code_cooldown: newCooldown || prev.refer_code_cooldown,
                } : prev);
              }}
            />

            {/* ============== Affiliate Code card REMOVED per user request ============== */}

            {/* ============== Earnings breakdown (only for affiliates) ============== */}
            {isAff && (
              <div
                className="rounded-2xl p-4"
                style={{
                  background: "linear-gradient(135deg,#101824 0%,#0b121c 100%)",
                  border: "1px solid rgba(192,132,252,0.22)",
                }}
              >
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-purple-300 font-bold mb-3">
                  <Crown size={12} /> Affiliate earnings
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <EarningStat
                    label="Paid Commissions"
                    value={`${symbol}${Number(data.affiliate_paid_commission).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                  />
                  <EarningStat
                    label="CPA Bonuses"
                    value={`${symbol}${Number(data.affiliate_cpa_paid).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                  />
                </div>
              </div>
            )}

            {/* ============== How it works (3 quick steps) ============== */}
            <div className="rounded-2xl bg-[#0f1622] border border-white/5 p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
                <Sparkles size={12} /> How it works
              </div>
              <div className="grid grid-cols-3 gap-2">
                <HowStep n="1" icon={Share2} title="Share" text="Send your code or link" color="#3b82f6" />
                <HowStep n="2" icon={Users} title="They play" text="Friend signs up & loses bets" color="#21c46e" />
                <HowStep n="3" icon={Gift} title="You earn" text={`${ngrPct.toFixed(0)}% of their daily NGR`} color="#ffc933" />
              </div>
            </div>

            {/* ============== Invitees (advanced: site+agent split + per-referee NGR) ============== */}
            <InviteesList
              invitees={data.invitees_recent || []}
              invitedTotal={data.invited_count}
              symbol={symbol}
              ngrPct={ngrPct}
            />
          </>
        )}
      </div>

      {/* Share modal — robust cross-browser share menu */}
      {shareInfo && (
        <ShareModal
          info={shareInfo}
          onClose={() => setShareInfo(null)}
          onCopy={async () => {
            const ok = await copyToClipboard(shareInfo.url);
            if (ok) toast.success("Link copied");
            else toast.error("Could not copy");
          }}
        />
      )}
    </ProfileShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Share Modal — platform deep-links + copy fallback                          */
/* -------------------------------------------------------------------------- */
function ShareModal({ info, onClose, onCopy }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const url = info.url;
  const text = info.text || info.title || "Join me on Maxlive88";
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  const fullMsg = encodeURIComponent(`${text}\n${url}`);

  const targets = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      color: "#25d366",
      icon: MessageCircle,
      href: `https://wa.me/?text=${fullMsg}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      color: "#26a5e4",
      icon: Send,
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      color: "#1877f2",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      id: "twitter",
      label: "X / Twitter",
      color: "#1da1f2",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      id: "email",
      label: "Email",
      color: "#94a3b8",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(info.title || "Maxlive88 invite")}&body=${fullMsg}`,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="referral-share-modal"
    >
      <div
        className="w-full sm:max-w-[400px] rounded-t-3xl sm:rounded-2xl overflow-hidden animate-[ref-sheet_.28s_cubic-bezier(.2,.8,.2,1)_both]"
        style={{
          background: "#0f1622",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 -24px 60px -20px rgba(0,0,0,0.5)",
        }}
      >
        <style>{`@keyframes ref-sheet { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: none; opacity: 1; } }`}</style>

        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-white/15" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div className="min-w-0">
            <h3 className="text-white font-bold text-[17px] leading-tight">Share your invite</h3>
            <p className="text-white/45 text-[12px] mt-0.5 truncate" title={url}>{url}</p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] transition shrink-0"
            data-testid="referral-share-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Platform grid */}
        <div className="px-3 pb-2 grid grid-cols-5 gap-1">
          {targets.map((t) => {
            const TIcon = t.icon;
            return (
              <a
                key={t.id}
                href={t.href}
                target={t.id === "email" ? "_self" : "_blank"}
                rel="noopener noreferrer"
                onClick={() => setTimeout(onClose, 200)}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl hover:bg-white/[0.04] transition active:scale-[0.97]"
                data-testid={`referral-share-${t.id}`}
              >
                <div
                  className="h-12 w-12 rounded-full inline-flex items-center justify-center"
                  style={{
                    background: `${t.color}1f`,
                    border: `1px solid ${t.color}55`,
                    color: t.color,
                  }}
                >
                  <TIcon size={22} strokeWidth={2.2} />
                </div>
                <span className="text-[11px] text-white/70 font-semibold truncate w-full text-center">
                  {t.label}
                </span>
              </a>
            );
          })}
        </div>

        {/* Copy link button */}
        <div className="px-4 pb-4 pt-1">
          <button
            onClick={() => { onCopy(); onClose(); }}
            className="w-full h-12 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 hover:bg-white/[0.07] transition active:scale-[0.985]"
            data-testid="referral-share-copy"
          >
            <Copy size={16} /> Copy link
          </button>
        </div>

        {/* iOS safe area */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}

/* ============================ Sub-components ============================ */

/* -------------------------------------------------------------------------- */
/*  CustomReferCodeCard — generate a unique custom referral code              */
/*  • Live availability check (debounced)                                     */
/*  • Custom share link preview + QR code (downloadable)                      */
/*  • Completely separate from Affiliate (MLF-XXXXXXX) and Promo systems —    */
/*    backend enforces global uniqueness across all three namespaces.         */
/* -------------------------------------------------------------------------- */
function CustomReferCodeCard({ currentCode, shareBase, initialCooldown, username, shareBonusText, onUpdated }) {
  const [input, setInput] = useState("");
  const [status, setStatus] = useState("idle"); // idle | checking | available | taken | invalid | own | cooldown
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [cooldown, setCooldown] = useState(initialCooldown || { locked: false, next_change_at: null, days_remaining: 0 });
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null); // { url, blob, file, kind } | null
  const debounceRef = useRef(null);
  const qrRef = useRef(null);

  // Keep local cooldown in sync if parent passes a fresh value (e.g. after first load).
  useEffect(() => {
    if (initialCooldown) setCooldown(initialCooldown);
  }, [initialCooldown]);

  const locked = !!cooldown?.locked;
  const daysRemaining = cooldown?.days_remaining || 0;

  const canonical = input.trim().toUpperCase();
  const base = (shareBase || "https://maxlive88.com/").replace(/\/$/, "") + "/";
  const previewUrl = canonical ? `${base}register?ref=${canonical}` : "";
  const liveShareUrl = currentCode ? `${base}register?ref=${currentCode}` : "";

  useEffect(() => {
    if (!input) {
      setStatus("idle");
      setReason("");
      return;
    }
    setStatus("checking");
    setReason("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/profile/referral/check-code", { params: { code: canonical } });
        if (data?.cooldown) setCooldown(data.cooldown); // stay in sync with server
        if (data?.available) {
          setStatus("available");
          setReason("");
        } else {
          const r = data?.reason || "Not available";
          if (/already your current/i.test(r)) setStatus("own");
          else if (/can change your code again/i.test(r)) setStatus("cooldown");
          else if (/taken|reserved|MLF prefix/i.test(r)) setStatus("taken");
          else setStatus("invalid");
          setReason(r);
        }
      } catch (e) {
        setStatus("invalid");
        setReason(e?.response?.data?.detail || "Check failed");
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [canonical]);

  const onSave = async () => {
    if (status !== "available" || saving || locked) return;
    setSaving(true);
    try {
      const { data } = await api.post("/profile/referral/set-code", { code: canonical });
      toast.success(`Referral code updated to ${data.code}`);
      if (data.cooldown) setCooldown(data.cooldown);
      onUpdated?.(data.code, data.share_url, data.cooldown);
      setInput("");
      setStatus("idle");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update code");
    } finally {
      setSaving(false);
    }
  };

  // Draws the M88 brand badge directly onto a 2D canvas at center of the QR.
  // Self-contained — no foreign SVG/image, so the canvas never gets tainted.
  // Knockout (excavate) is already implicit: QR error correction = "H" covers the area.
  const roundRectPath = (ctx, x, y, w, h, r) => {
    const rr = Math.min(r, Math.min(w, h) / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y,     x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x,     y + h, rr);
    ctx.arcTo(x,     y + h, x,     y,     rr);
    ctx.arcTo(x,     y,     x + w, y,     rr);
    ctx.closePath();
  };

  const drawBrandBadge = (ctx, cx, cy, size) => {
    const r = size * 0.22;
    // outer white ring
    ctx.fillStyle = "#ffffff";
    const w = size, h = size, x = cx - w / 2, y = cy - h / 2;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y,     x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x,     y + h, r);
    ctx.arcTo(x,     y + h, x,     y,     r);
    ctx.arcTo(x,     y,     x + w, y,     r);
    ctx.closePath();
    ctx.fill();
    // inner dark square
    const inset = size * 0.08;
    const ix = x + inset, iy = y + inset, iw = w - inset * 2, ih = h - inset * 2, ir = r * 0.72;
    ctx.fillStyle = "#0a1119";
    ctx.beginPath();
    ctx.moveTo(ix + ir, iy);
    ctx.arcTo(ix + iw, iy,      ix + iw, iy + ih, ir);
    ctx.arcTo(ix + iw, iy + ih, ix,      iy + ih, ir);
    ctx.arcTo(ix,      iy + ih, ix,      iy,      ir);
    ctx.arcTo(ix,      iy,      ix + iw, iy,      ir);
    ctx.closePath();
    ctx.fill();
    // "M" white
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const fontFamily = "'Arial Black', Helvetica, sans-serif";
    ctx.font = `900 ${Math.round(size * 0.34)}px ${fontFamily}`;
    ctx.fillStyle = "#ffffff";
    ctx.fillText("M", cx, cy - size * 0.14);
    // "88" red
    ctx.font = `900 ${Math.round(size * 0.32)}px ${fontFamily}`;
    ctx.fillStyle = "#ff2740";
    ctx.fillText("88", cx, cy + size * 0.18);
  };

  // Renders a square branded QR PNG entirely on canvas (no SVG → no taint).
  const renderQRToCanvas = async (text, sizePx) => {
    const canvas = document.createElement("canvas");
    canvas.width = sizePx;
    canvas.height = sizePx;
    await QRCode.toCanvas(canvas, text, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: sizePx,
      color: { dark: "#0a1119", light: "#ffffff" },
    });
    // Draw brand badge on top (~22% of QR size)
    const ctx = canvas.getContext("2d");
    const badge = Math.round(sizePx * 0.22);
    drawBrandBadge(ctx, sizePx / 2, sizePx / 2, badge);
    return canvas;
  };

  const onDownloadQR = async () => {
    try {
      const canvas = await renderQRToCanvas(liveShareUrl || `${base}register`, 720);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error("canvas export failed")), "image/png", 0.95);
      });
      const url = URL.createObjectURL(blob);
      const file = new File([blob], `maxlive88-referral-${currentCode}.png`, { type: "image/png" });
      setPreview({ url, blob, file, kind: "qr" });
    } catch (e) {
      toast.error(e?.message || "Could not generate QR");
    }
  };

  // ============== Ultra-Professional PDF (A4 portrait) ==============
  // Premium-grade brand flyer designed for email, print and formal sharing.
  // Layout: branded letterhead → decorative accent rule → hero title block →
  // premium QR card (multi-layer shadow + corner accents) → BONUS ribbon →
  // gradient-effect referral code pill → "Invited by" footer → numbered
  // step badges → disclaimer → branded foot bar with page metadata.
  //
  // NOTE: We ALSO render a JPEG preview-thumbnail to use as the modal's
  // visible image because Chrome blocks iframes that point at a blob: PDF
  // (security policy). The Save button still hands off the real PDF blob.
  const onDownloadPDF = async () => {
    if (!currentCode) return;
    setExporting(true);
    try {
      // Render QR to a high-res canvas so it stays crisp in the PDF
      const qrCanvas = await renderQRToCanvas(liveShareUrl, 900);
      const qrDataUrl = qrCanvas.toDataURL("image/png");

      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
      pdf.setProperties({
        title: `Maxlive88 referral – ${currentCode}`,
        subject: "Invite & Earn — 100% Welcome Bonus",
        author: username ? `@${username}` : "Maxlive88 user",
        creator: "Maxlive88",
        keywords: "Maxlive88, referral, invite, bonus, signup",
      });

      const W = 210, H = 297; // A4 mm
      // Brand palette
      const BRAND_DARK = [10, 17, 25];
      const BRAND_DARK_2 = [14, 24, 40];
      const BRAND_DARK_3 = [22, 36, 58];
      const BRAND_GREEN = [33, 196, 110];
      const BRAND_GREEN_LIGHT = [56, 230, 142];
      const BRAND_RED = [255, 39, 64];
      const BRAND_GOLD = [255, 201, 51];
      const TEXT_PRIMARY = [255, 255, 255];
      const TEXT_MUTED = [180, 195, 215];
      const TEXT_DIM = [130, 145, 165];

      // ===== Background — dark brand canvas with subtle gradient bands =====
      pdf.setFillColor(...BRAND_DARK);
      pdf.rect(0, 0, W, H, "F");
      // top-right brand-red glow (simulated with overlapping rounded rect)
      pdf.setFillColor(255, 39, 64);
      pdf.setGState(new pdf.GState({ opacity: 0.07 }));
      pdf.circle(W - 10, 20, 70, "F");
      // bottom-left brand-green glow
      pdf.setFillColor(33, 196, 110);
      pdf.setGState(new pdf.GState({ opacity: 0.06 }));
      pdf.circle(10, H - 20, 80, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));

      // ===== Premium letterhead (gradient band) =====
      // Layer 1 — base band
      pdf.setFillColor(...BRAND_DARK_2);
      pdf.rect(0, 0, W, 32, "F");
      // Layer 2 — top highlight strip
      pdf.setFillColor(...BRAND_DARK_3);
      pdf.rect(0, 0, W, 4, "F");
      // Layer 3 — gold accent line at bottom of band
      pdf.setFillColor(...BRAND_GOLD);
      pdf.rect(0, 31.4, W, 0.6, "F");
      // Layer 4 — green accent line
      pdf.setFillColor(...BRAND_GREEN);
      pdf.rect(0, 32, W, 0.4, "F");

      // M88 brand badge (square logo on the left)
      const badgeSize = 18;
      const badgeX = 14, badgeY = 7;
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(badgeX, badgeY, badgeSize, badgeSize, 3, 3, "F");
      pdf.setFillColor(...BRAND_DARK);
      pdf.roundedRect(badgeX + 1.4, badgeY + 1.4, badgeSize - 2.8, badgeSize - 2.8, 2.2, 2.2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text("M", badgeX + badgeSize / 2, badgeY + 9.5, { align: "center" });
      pdf.setTextColor(...BRAND_RED);
      pdf.setFontSize(10);
      pdf.text("88", badgeX + badgeSize / 2, badgeY + 15, { align: "center" });

      // Wordmark "Maxlive88" next to badge
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(22);
      pdf.setTextColor(255, 255, 255);
      pdf.text("Maxlive", badgeX + badgeSize + 5, 18);
      const mxW = pdf.getTextWidth("Maxlive");
      pdf.setTextColor(...BRAND_RED);
      pdf.text("88", badgeX + badgeSize + 5 + mxW, 18);
      // Tagline below wordmark
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(180, 195, 215);
      pdf.text("PLAY SMART  ·  WIN BIG", badgeX + badgeSize + 5, 23);

      // Right-side eyebrow chip
      pdf.setFillColor(255, 201, 51);
      const chipW = 38, chipH = 7.5;
      const chipX = W - chipW - 14;
      const chipY = 12;
      pdf.roundedRect(chipX, chipY, chipW, chipH, 3.2, 3.2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(...BRAND_DARK);
      pdf.text("INVITE & EARN", chipX + chipW / 2, chipY + 5.2, { align: "center" });
      // small "ref code" hint under chip
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(180, 195, 215);
      pdf.text(`REF · ${currentCode}`, chipX + chipW, 24.5, { align: "right" });

      // ===== Hero block (left column) =====
      const heroY = 50;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(30);
      pdf.setTextColor(255, 255, 255);
      pdf.text("Join me on", 16, heroY);
      pdf.setTextColor(...BRAND_GREEN_LIGHT);
      pdf.text("Maxlive88.", 16, heroY + 12);

      // Decorative accent rule under title
      pdf.setFillColor(...BRAND_GOLD);
      pdf.rect(16, heroY + 16, 24, 0.9, "F");
      pdf.setFillColor(...BRAND_GREEN);
      pdf.rect(42, heroY + 16, 8, 0.9, "F");

      // Subtitle
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10.5);
      pdf.setTextColor(...TEXT_MUTED);
      pdf.text("Sign up with my code to claim a", 16, heroY + 24);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...BRAND_GOLD);
      pdf.text("100% welcome bonus", 16, heroY + 30);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...TEXT_MUTED);
      pdf.text("on your first deposit — instantly.", 16, heroY + 36);

      // ===== Value-prop bullets =====
      const bullets = [
        ["Instant 100% deposit bonus", "First top-up matched 1:1"],
        ["Real-time withdrawals", "bKash · Nagad · Rocket · Upay"],
        ["24x7 Bengali live support", "Native chat in Bangla & English"],
        ["Daily NGR cashback", "Earn back on every loss"],
      ];
      let by = heroY + 48;
      const bulletColW = 92; // mm — left column
      for (const [title, sub] of bullets) {
        // green dot
        pdf.setFillColor(...BRAND_GREEN);
        pdf.circle(18, by - 1.4, 1.4, "F");
        // title
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.text(title, 23, by);
        // subtitle
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        pdf.setTextColor(...TEXT_MUTED);
        pdf.text(sub, 23, by + 4.5);
        by += 11;
      }

      // ===== QR card (right column) — multi-layer premium card =====
      const qrSide = 64; // mm
      const qrX = W - qrSide - 18;
      const qrY = heroY + 6;
      // shadow layer 1 (soft, offset down-right)
      pdf.setFillColor(0, 0, 0);
      pdf.setGState(new pdf.GState({ opacity: 0.35 }));
      pdf.roundedRect(qrX - 4 + 2, qrY - 4 + 3, qrSide + 8, qrSide + 8, 4, 4, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));
      // gold rim
      pdf.setFillColor(...BRAND_GOLD);
      pdf.roundedRect(qrX - 5, qrY - 5, qrSide + 10, qrSide + 10, 4.5, 4.5, "F");
      // white card
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(qrX - 4, qrY - 4, qrSide + 8, qrSide + 8, 4, 4, "F");
      // QR image
      pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSide, qrSide, undefined, "FAST");
      // Corner accents (4 small L-shapes inside the white card)
      pdf.setDrawColor(...BRAND_GREEN);
      pdf.setLineWidth(0.6);
      const ax = qrX - 2.5, ay = qrY - 2.5, ae = qrSide + 5;
      // top-left
      pdf.line(ax, ay + 4, ax, ay); pdf.line(ax, ay, ax + 4, ay);
      // top-right
      pdf.line(ax + ae - 4, ay, ax + ae, ay); pdf.line(ax + ae, ay, ax + ae, ay + 4);
      // bottom-left
      pdf.line(ax, ay + ae - 4, ax, ay + ae); pdf.line(ax, ay + ae, ax + 4, ay + ae);
      // bottom-right
      pdf.line(ax + ae - 4, ay + ae, ax + ae, ay + ae); pdf.line(ax + ae, ay + ae - 4, ax + ae, ay + ae);

      // "Scan to join" caption under QR card
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...BRAND_GOLD);
      pdf.text("SCAN TO JOIN", qrX + qrSide / 2, qrY + qrSide + 10, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(...TEXT_MUTED);
      pdf.text("Camera ready · iOS & Android", qrX + qrSide / 2, qrY + qrSide + 14, { align: "center" });

      // BONUS ribbon — rotated tag clipped on top-right corner of QR card
      const stampText = (shareBonusText || "").trim();
      if (stampText) {
        pdf.saveGraphicsState();
        // angle ~ -10 degrees
        const cx = qrX + qrSide - 2;
        const cy = qrY - 1;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        const tw = pdf.getTextWidth(stampText) + 8;
        const th = 9;
        // shadow
        pdf.setFillColor(0, 0, 0);
        pdf.setGState(new pdf.GState({ opacity: 0.4 }));
        pdf.roundedRect(cx - tw / 2 + 0.6, cy - th / 2 + 0.8, tw, th, 1.8, 1.8, "F");
        pdf.setGState(new pdf.GState({ opacity: 1 }));
        // main ribbon
        pdf.setFillColor(...BRAND_RED);
        pdf.roundedRect(cx - tw / 2, cy - th / 2, tw, th, 1.8, 1.8, "F");
        // top highlight
        pdf.setFillColor(255, 90, 110);
        pdf.roundedRect(cx - tw / 2, cy - th / 2, tw, th / 2.2, 1.8, 1.8, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.text(stampText, cx, cy + 1.8, { align: "center" });
        pdf.restoreGraphicsState();
      }

      // ===== Premium referral code pill (multi-layer with depth) =====
      const pillW = 178, pillH = 40;
      const pillX = (W - pillW) / 2;
      const pillY = heroY + 116;
      // outer shadow
      pdf.setFillColor(0, 0, 0);
      pdf.setGState(new pdf.GState({ opacity: 0.5 }));
      pdf.roundedRect(pillX, pillY + 2.5, pillW, pillH, 7, 7, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));
      // gold rim
      pdf.setFillColor(...BRAND_GOLD);
      pdf.roundedRect(pillX - 0.8, pillY - 0.8, pillW + 1.6, pillH + 1.6, 7.4, 7.4, "F");
      // gradient simulation — 6 stacked rects from light to dark green
      const grad = [
        [76, 240, 152], [56, 226, 138], [40, 210, 122],
        [33, 196, 110], [26, 174, 96], [18, 140, 76],
      ];
      const bands = grad.length;
      for (let i = 0; i < bands; i++) {
        pdf.setFillColor(...grad[i]);
        const bh = pillH / bands;
        if (i === 0) {
          pdf.roundedRect(pillX, pillY, pillW, bh + 0.5, 7, 7, "F");
        } else if (i === bands - 1) {
          pdf.roundedRect(pillX, pillY + bh * i - 0.5, pillW, bh + 0.5, 7, 7, "F");
        } else {
          pdf.rect(pillX, pillY + bh * i - 0.3, pillW, bh + 0.6, "F");
        }
      }
      // inner top highlight
      pdf.setFillColor(255, 255, 255);
      pdf.setGState(new pdf.GState({ opacity: 0.18 }));
      pdf.roundedRect(pillX + 2, pillY + 2, pillW - 4, pillH / 2 - 1, 5.5, 5.5, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));
      // Label with decorative dashes
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(255, 255, 255);
      const labelText = "REFERRAL CODE";
      pdf.text(labelText, W / 2, pillY + 9, { align: "center" });
      // decorative dashes left and right of label
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.4);
      const lblHalfW = pdf.getTextWidth(labelText) / 2;
      pdf.line(W / 2 - lblHalfW - 14, pillY + 8, W / 2 - lblHalfW - 4, pillY + 8);
      pdf.line(W / 2 + lblHalfW + 4, pillY + 8, W / 2 + lblHalfW + 14, pillY + 8);
      // Code — big and bold
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(26);
      pdf.setTextColor(...BRAND_DARK);
      pdf.text(currentCode, W / 2, pillY + 28, { align: "center" });

      // ===== Invited by + URL =====
      const handle = username ? `@${username}` : "";
      if (handle) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(...TEXT_MUTED);
        pdf.text(`Invited by ${handle}`, W / 2, pillY + pillH + 11, { align: "center" });
      }
      // URL chip
      const urlText = liveShareUrl || "";
      pdf.setFont("courier", "bold");
      pdf.setFontSize(10);
      const urlTextW = pdf.getTextWidth(urlText);
      const urlChipW = urlTextW + 12;
      const urlChipH = 9;
      const urlChipX = (W - urlChipW) / 2;
      const urlChipY = pillY + pillH + (handle ? 16 : 11);
      pdf.setFillColor(...BRAND_DARK_3);
      pdf.roundedRect(urlChipX, urlChipY, urlChipW, urlChipH, 2.2, 2.2, "F");
      pdf.setDrawColor(...BRAND_GOLD);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(urlChipX, urlChipY, urlChipW, urlChipH, 2.2, 2.2, "S");
      pdf.setTextColor(...BRAND_GOLD);
      pdf.text(urlText, W / 2, urlChipY + 6, { align: "center" });

      // ===== How to redeem — numbered step badges =====
      const stepsY = urlChipY + urlChipH + 18;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.setTextColor(255, 255, 255);
      pdf.text("How to redeem", 16, stepsY);
      // green underline accent
      pdf.setFillColor(...BRAND_GREEN);
      pdf.rect(16, stepsY + 1.6, 22, 0.7, "F");

      const steps = [
        ["Scan the QR", "Open your phone camera and point at the QR above."],
        ["Sign up", "Code auto-fills — tap Create account to continue."],
        ["Deposit & play", "Make your first deposit to claim the 100% bonus."],
      ];
      let sy = stepsY + 11;
      for (let i = 0; i < steps.length; i++) {
        const [t, s] = steps[i];
        // numbered circle
        pdf.setFillColor(...BRAND_GREEN);
        pdf.circle(20, sy, 4, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(...BRAND_DARK);
        pdf.text(String(i + 1), 20, sy + 1.5, { align: "center" });
        // step title
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.text(t, 27, sy - 0.5);
        // step description
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.8);
        pdf.setTextColor(...TEXT_MUTED);
        pdf.text(s, 27, sy + 4);
        sy += 12;
      }

      // ===== Footer brand bar (multi-band) =====
      // Disclaimer above footer
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(7);
      pdf.setTextColor(...TEXT_DIM);
      pdf.text(
        "18+ only. Gamble responsibly. Bonus terms apply — visit maxlive88.com for full details.",
        W / 2, H - 18, { align: "center" }
      );
      // Top accent strip (gold)
      pdf.setFillColor(...BRAND_GOLD);
      pdf.rect(0, H - 14, W, 0.5, "F");
      // Footer band
      pdf.setFillColor(...BRAND_DARK_2);
      pdf.rect(0, H - 13.5, W, 13.5, "F");
      // Footer left — brand
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text("MAXLIVE88", 14, H - 8);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(...TEXT_MUTED);
      pdf.text("Play Smart  ·  Win Big", 14, H - 3.5);
      // Footer center — domain
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(...BRAND_GOLD);
      pdf.text("maxlive88.com", W / 2, H - 6, { align: "center" });
      // Footer right — share code
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`CODE · ${currentCode}`, W - 14, H - 8, { align: "right" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(...TEXT_MUTED);
      const issued = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      pdf.text(`Issued ${issued}`, W - 14, H - 3.5, { align: "right" });

      // PDF blob (for save / share)
      const pdfBlob = pdf.output("blob");
      const pdfFile = new File([pdfBlob], `maxlive88-referral-${currentCode}.pdf`, { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Visual PDF-thumbnail rendered onto a canvas (Chrome blocks iframe
      // blob: PDFs, so we show this image as the modal's preview instead).
      const thumbCanvas = await renderPDFThumbnail({
        currentCode, liveShareUrl, username, shareBonusText, qrCanvas,
      });
      const thumbBlob = await new Promise((resolve, reject) => {
        thumbCanvas.toBlob((b) => b ? resolve(b) : reject(new Error("preview render failed")), "image/jpeg", 0.92);
      });
      const thumbUrl = URL.createObjectURL(thumbBlob);

      setPreview({
        url: thumbUrl,        // visible preview image
        blob: pdfBlob,
        file: pdfFile,
        downloadUrl: pdfUrl,  // separate URL that points at the real PDF
        kind: "pdf",
      });
    } catch (e) {
      toast.error(e?.message || "Could not generate PDF");
    } finally {
      setExporting(false);
    }
  };

  // Render an A4-proportioned JPEG that visually represents the PDF, for use
  // as the preview image inside the SharePreviewModal. We replicate the same
  // brand DNA at a smaller pixel size so it doesn't bloat the page.
  const renderPDFThumbnail = async ({ currentCode: ccode, liveShareUrl: lurl, username: uname, shareBonusText: bonus, qrCanvas }) => {
    // A4 ratio = 210/297 ≈ 0.707. Use 720×1018 for crisp on-screen rendering.
    const W = 720, H = 1018;
    const cnv = document.createElement("canvas");
    cnv.width = W; cnv.height = H;
    const ctx = cnv.getContext("2d");

    // Background — dark brand
    ctx.fillStyle = "#0a1119";
    ctx.fillRect(0, 0, W, H);
    // soft glow top-right
    const glowR = ctx.createRadialGradient(W * 0.95, 60, 8, W * 0.95, 60, 280);
    glowR.addColorStop(0, "rgba(255,39,64,0.22)");
    glowR.addColorStop(1, "rgba(255,39,64,0)");
    ctx.fillStyle = glowR; ctx.fillRect(0, 0, W, H);
    // soft glow bottom-left
    const glowG = ctx.createRadialGradient(40, H - 60, 8, 40, H - 60, 320);
    glowG.addColorStop(0, "rgba(33,196,110,0.22)");
    glowG.addColorStop(1, "rgba(33,196,110,0)");
    ctx.fillStyle = glowG; ctx.fillRect(0, 0, W, H);

    // ===== Top letterhead band =====
    ctx.fillStyle = "#0e1828";
    ctx.fillRect(0, 0, W, 110);
    // top highlight
    ctx.fillStyle = "#16243a";
    ctx.fillRect(0, 0, W, 14);
    // gold accent line
    ctx.fillStyle = "#ffc933";
    ctx.fillRect(0, 108, W, 2.5);
    ctx.fillStyle = "#21c46e";
    ctx.fillRect(0, 110, W, 1.5);

    // M88 brand badge
    const bX = 48, bY = 28, bS = 56;
    ctx.fillStyle = "#ffffff";
    roundRectPath(ctx, bX, bY, bS, bS, 9); ctx.fill();
    ctx.fillStyle = "#0a1119";
    roundRectPath(ctx, bX + 4, bY + 4, bS - 8, bS - 8, 6.5); ctx.fill();
    ctx.font = "900 22px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("M", bX + bS / 2, bY + bS / 2 - 8);
    ctx.font = "900 20px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ff2740";
    ctx.fillText("88", bX + bS / 2, bY + bS / 2 + 10);

    // Wordmark next to badge
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    ctx.font = "900 44px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText("Maxlive", bX + bS + 14, 64);
    const mxW = ctx.measureText("Maxlive").width;
    ctx.fillStyle = "#ff2740";
    ctx.fillText("88", bX + bS + 14 + mxW, 64);
    // Tagline
    ctx.font = "600 13px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.95)";
    ctx.fillText("PLAY SMART  ·  WIN BIG", bX + bS + 14, 84);

    // Right eyebrow chip
    ctx.font = "900 19px 'Arial Black', Helvetica, sans-serif";
    const chipText = "INVITE & EARN";
    ctx.textAlign = "right"; ctx.textBaseline = "alphabetic";
    const chipTextW = ctx.measureText(chipText).width;
    const chipPad = 16;
    const chipH = 28;
    const chipR = 7;
    const chipX = W - 48 - chipTextW - chipPad * 2;
    const chipY = 44;
    ctx.fillStyle = "#ffc933";
    roundRectPath(ctx, chipX, chipY, chipTextW + chipPad * 2, chipH, chipR); ctx.fill();
    ctx.fillStyle = "#0a1119";
    ctx.textAlign = "center";
    ctx.fillText(chipText, chipX + (chipTextW + chipPad * 2) / 2, chipY + 20);
    // ref code hint
    ctx.font = "600 13px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.95)";
    ctx.textAlign = "right";
    ctx.fillText(`REF · ${ccode}`, W - 48, 92);

    // ===== Hero title =====
    ctx.textAlign = "left";
    ctx.font = "900 58px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText("Join me on", 48, 200);
    ctx.fillStyle = "#38e68e";
    ctx.fillText("Maxlive88.", 48, 262);

    // Decorative accent rules under title
    ctx.fillStyle = "#ffc933"; ctx.fillRect(48, 274, 80, 4);
    ctx.fillStyle = "#21c46e"; ctx.fillRect(132, 274, 24, 4);

    // Subtitle
    ctx.font = "500 22px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.95)";
    ctx.fillText("Sign up with my code to claim a", 48, 308);
    ctx.font = "800 22px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#ffc933";
    ctx.fillText("100% welcome bonus", 48, 336);
    ctx.font = "500 22px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.95)";
    ctx.fillText("on your first deposit — instantly.", 48, 364);

    // ===== Two-column bullets (left col) =====
    const bullets = [
      ["Instant 100% deposit bonus", "First top-up matched 1:1"],
      ["Real-time withdrawals", "bKash · Nagad · Rocket · Upay"],
      ["24x7 Bengali live support", "Native chat in Bangla & English"],
      ["Daily NGR cashback", "Earn back on every loss"],
    ];
    let by = 410;
    for (const [t, s] of bullets) {
      // green dot
      ctx.fillStyle = "#21c46e";
      ctx.beginPath(); ctx.arc(58, by - 4, 5, 0, Math.PI * 2); ctx.fill();
      // title
      ctx.font = "800 18px Helvetica, Arial, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.fillText(t, 74, by);
      // subtitle
      ctx.font = "500 14px Helvetica, Arial, sans-serif";
      ctx.fillStyle = "rgba(180,195,215,0.9)";
      ctx.fillText(s, 74, by + 18);
      by += 42;
    }

    // ===== QR card (right column) =====
    const qrCardSize = 230;
    const qrCardX = W - qrCardSize - 48;
    const qrCardY = 410;
    // shadow
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 8;
    // gold rim
    ctx.fillStyle = "#ffc933";
    roundRectPath(ctx, qrCardX - 6, qrCardY - 6, qrCardSize + 12, qrCardSize + 12, 16); ctx.fill();
    ctx.restore();
    ctx.fillStyle = "#ffffff";
    roundRectPath(ctx, qrCardX, qrCardY, qrCardSize, qrCardSize, 14); ctx.fill();
    const qrInner = qrCardSize - 28;
    ctx.drawImage(qrCanvas, qrCardX + 14, qrCardY + 14, qrInner, qrInner);
    // corner accents
    ctx.strokeStyle = "#21c46e";
    ctx.lineWidth = 2.5;
    const aX = qrCardX + 3, aY = qrCardY + 3, aE = qrCardSize - 6;
    ctx.beginPath();
    ctx.moveTo(aX, aY + 14); ctx.lineTo(aX, aY); ctx.lineTo(aX + 14, aY);
    ctx.moveTo(aX + aE - 14, aY); ctx.lineTo(aX + aE, aY); ctx.lineTo(aX + aE, aY + 14);
    ctx.moveTo(aX, aY + aE - 14); ctx.lineTo(aX, aY + aE); ctx.lineTo(aX + 14, aY + aE);
    ctx.moveTo(aX + aE - 14, aY + aE); ctx.lineTo(aX + aE, aY + aE); ctx.lineTo(aX + aE, aY + aE - 14);
    ctx.stroke();

    // scan caption
    ctx.font = "900 14px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffc933";
    ctx.textAlign = "center";
    ctx.fillText("SCAN TO JOIN", qrCardX + qrCardSize / 2, qrCardY + qrCardSize + 28);
    ctx.font = "500 12px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.85)";
    ctx.fillText("Camera ready · iOS & Android", qrCardX + qrCardSize / 2, qrCardY + qrCardSize + 46);

    // BONUS ribbon
    if (bonus) {
      ctx.save();
      ctx.translate(qrCardX + qrCardSize - 4, qrCardY - 4);
      ctx.rotate(-8 * Math.PI / 180);
      ctx.font = "900 18px 'Arial Black', Helvetica, sans-serif";
      const tw = ctx.measureText(bonus).width + 22;
      const th = 34;
      const sr = 8;
      // shadow
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 3;
      ctx.fillStyle = "#ff2740";
      roundRectPath(ctx, -tw / 2, -th / 2, tw, th, sr); ctx.fill();
      ctx.shadowColor = "transparent"; ctx.shadowBlur = 0;
      // top highlight
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      roundRectPath(ctx, -tw / 2 + 2, -th / 2 + 2, tw - 4, th / 2 - 1, sr - 2); ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(bonus, 0, 2);
      ctx.restore();
      ctx.textBaseline = "alphabetic";
    }

    // ===== Premium code pill (with depth) =====
    const pillW = 600, pillH = 110;
    const pillX = (W - pillW) / 2;
    const pillY = 700;
    // shadow
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 26;
    ctx.shadowOffsetY = 10;
    // gold rim
    ctx.fillStyle = "#ffc933";
    roundRectPath(ctx, pillX - 4, pillY - 4, pillW + 8, pillH + 8, 24); ctx.fill();
    ctx.restore();
    // gradient body
    const pillGrad = ctx.createLinearGradient(pillX, pillY, pillX, pillY + pillH);
    pillGrad.addColorStop(0, "#4cf098");
    pillGrad.addColorStop(0.45, "#28d27a");
    pillGrad.addColorStop(1, "#128c4c");
    ctx.fillStyle = pillGrad;
    roundRectPath(ctx, pillX, pillY, pillW, pillH, 22); ctx.fill();
    // inner top highlight
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = "#ffffff";
    roundRectPath(ctx, pillX + 4, pillY + 4, pillW - 8, pillH / 2 - 4, 18); ctx.fill();
    ctx.restore();
    // label with dashes
    ctx.font = "900 16px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.fillText("REFERRAL CODE", W / 2, pillY + 32);
    const lblW = ctx.measureText("REFERRAL CODE").width;
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fillRect(W / 2 - lblW / 2 - 50, pillY + 28, 36, 2);
    ctx.fillRect(W / 2 + lblW / 2 + 14, pillY + 28, 36, 2);
    // Code
    ctx.font = "900 46px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#0a1119";
    ctx.fillText(ccode, W / 2, pillY + 88);

    // ===== Invited by + URL chip =====
    if (uname) {
      ctx.font = "700 20px Helvetica, Arial, sans-serif";
      ctx.fillStyle = "rgba(180,195,215,0.95)";
      ctx.textAlign = "center";
      ctx.fillText(`Invited by @${uname}`, W / 2, pillY + pillH + 36);
    }
    // URL chip
    let txt = (lurl || "");
    ctx.font = "700 16px 'Menlo', 'Courier New', monospace";
    const urlW = ctx.measureText(txt).width + 32;
    const urlH = 30;
    const urlX = (W - urlW) / 2;
    const urlY = pillY + pillH + (uname ? 54 : 36);
    ctx.fillStyle = "#16243a";
    roundRectPath(ctx, urlX, urlY, urlW, urlH, 7); ctx.fill();
    ctx.strokeStyle = "#ffc933";
    ctx.lineWidth = 1.2;
    roundRectPath(ctx, urlX, urlY, urlW, urlH, 7); ctx.stroke();
    ctx.fillStyle = "#ffc933";
    ctx.textAlign = "center";
    ctx.fillText(txt, W / 2, urlY + 21);

    // ===== Footer brand bar =====
    ctx.fillStyle = "#ffc933";
    ctx.fillRect(0, H - 46, W, 2);
    ctx.fillStyle = "#0e1828";
    ctx.fillRect(0, H - 44, W, 44);
    ctx.font = "900 14px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.fillText("MAXLIVE88", 48, H - 26);
    ctx.font = "500 12px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.85)";
    ctx.fillText("Play Smart  ·  Win Big", 48, H - 10);
    // center
    ctx.font = "900 14px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffc933";
    ctx.textAlign = "center";
    ctx.fillText("maxlive88.com", W / 2, H - 18);
    // right
    ctx.font = "900 14px 'Arial Black', Helvetica, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "right";
    ctx.fillText(`CODE · ${ccode}`, W - 48, H - 26);
    ctx.font = "500 12px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(180,195,215,0.85)";
    const issued = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    ctx.fillText(`Issued ${issued}`, W - 48, H - 10);

    return cnv;
  };

  const onCopyLiveLink = async () => {
    const ok = await copyToClipboard(liveShareUrl);
    if (ok) toast.success("Link copied"); else toast.error("Could not copy");
  };

  // Compose a professional 1080×1350 (Instagram story-friendly) share card
  // with brand header, big QR, code highlight, share URL footer and the user's
  // handle. Fully canvas-native — no foreign SVG embedded — so toDataURL is
  // never blocked by a tainted-canvas error in Safari / mobile WebViews.
  const onDownloadShareCard = async () => {
    if (!currentCode) return;
    setExporting(true);
    try {
      // 1080×1500 — a touch taller than the previous 1350 so the username +
      // share URL never overlap with the bottom "Play Smart / Win Big" bar.
      const W = 1080, H = 1500;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      // Background — vertical brand gradient + soft red/green corner glows
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#0e1828");
      bg.addColorStop(0.55, "#0a1119");
      bg.addColorStop(1, "#070a10");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      const glow = ctx.createRadialGradient(W * 0.85, H * 0.12, 20, W * 0.85, H * 0.12, 520);
      glow.addColorStop(0, "rgba(255,39,64,0.32)");
      glow.addColorStop(1, "rgba(255,39,64,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);
      const glow2 = ctx.createRadialGradient(W * 0.15, H * 0.92, 20, W * 0.15, H * 0.92, 560);
      glow2.addColorStop(0, "rgba(25,165,92,0.28)");
      glow2.addColorStop(1, "rgba(25,165,92,0)");
      ctx.fillStyle = glow2;
      ctx.fillRect(0, 0, W, H);

      // Top brand strip — "Maxlive88" wordmark
      ctx.font = "900 70px 'Arial Black', Helvetica, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.fillText("Maxlive", 80, 130);
      ctx.fillStyle = "#ff2740";
      ctx.fillText("88", 80 + ctx.measureText("Maxlive").width, 130);

      // Eyebrow label
      ctx.font = "700 28px 'Arial Black', Helvetica, sans-serif";
      ctx.fillStyle = "#ffc933";
      ctx.textAlign = "right";
      ctx.fillText("INVITE & EARN", W - 80, 130);

      // Title
      ctx.font = "900 64px 'Arial Black', Helvetica, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.fillText("Join me on", 80, 240);
      ctx.fillStyle = "#21c46e";
      ctx.fillText("Maxlive88", 80, 320);

      // Subtitle
      ctx.font = "500 30px Helvetica, Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      ctx.fillText("Scan the QR or use my code below", 80, 370);

      // QR white card (centered) — uses canvas-native QR (no SVG)
      const qrCardSize = 580;
      const qrCardX = (W - qrCardSize) / 2;
      const qrCardY = 430;
      // shadow first
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.55)";
      ctx.shadowBlur = 48;
      ctx.shadowOffsetY = 14;
      ctx.fillStyle = "#ffffff";
      const r = 28;
      const x = qrCardX, y = qrCardY, w = qrCardSize, h = qrCardSize;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y,     x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x,     y + h, r);
      ctx.arcTo(x,     y + h, x,     y,     r);
      ctx.arcTo(x,     y,     x + w, y,     r);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Render QR onto its own canvas, then composite onto the card
      const qrInnerSize = qrCardSize - 80;
      const qrCanvas = await renderQRToCanvas(liveShareUrl, qrInnerSize);
      ctx.drawImage(qrCanvas, qrCardX + 40, qrCardY + 40, qrInnerSize, qrInnerSize);

      // BONUS sticker — sits OUTSIDE the upper-right corner of the QR card so
      // it never overlaps the QR positioning markers / scan area.
      const stampText = (shareBonusText || "").trim();
      if (stampText) {
        ctx.save();
        const sx = qrCardX + qrCardSize + 30; // 30px to the right of the card
        const sy = qrCardY - 10;              // 10px above the card top edge
        ctx.translate(sx, sy);
        ctx.rotate(-14 * Math.PI / 180);

        // Measure width based on the text
        ctx.font = "900 38px 'Arial Black', Helvetica, sans-serif";
        const padX = 22, padY = 14;
        const tw = ctx.measureText(stampText).width;
        const stW = tw + padX * 2;
        const stH = 60;

        // outer ring
        ctx.fillStyle = "#ff2740";
        ctx.shadowColor = "rgba(255,39,64,0.55)";
        ctx.shadowBlur = 30;
        ctx.shadowOffsetY = 6;
        const sr = 14;
        ctx.beginPath();
        ctx.moveTo(-stW / 2 + sr, -stH / 2);
        ctx.arcTo( stW / 2, -stH / 2,  stW / 2,  stH / 2, sr);
        ctx.arcTo( stW / 2,  stH / 2, -stW / 2,  stH / 2, sr);
        ctx.arcTo(-stW / 2,  stH / 2, -stW / 2, -stH / 2, sr);
        ctx.arcTo(-stW / 2, -stH / 2,  stW / 2, -stH / 2, sr);
        ctx.closePath();
        ctx.fill();
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        // inner dashed white border for "sticker" feel
        ctx.strokeStyle = "rgba(255,255,255,0.75)";
        ctx.lineWidth = 2.5;
        ctx.setLineDash([6, 4]);
        const isr = sr - 4;
        ctx.beginPath();
        ctx.moveTo(-stW / 2 + 8 + isr, -stH / 2 + 8);
        ctx.arcTo( stW / 2 - 8, -stH / 2 + 8,  stW / 2 - 8,  stH / 2 - 8, isr);
        ctx.arcTo( stW / 2 - 8,  stH / 2 - 8, -stW / 2 + 8,  stH / 2 - 8, isr);
        ctx.arcTo(-stW / 2 + 8,  stH / 2 - 8, -stW / 2 + 8, -stH / 2 + 8, isr);
        ctx.arcTo(-stW / 2 + 8, -stH / 2 + 8,  stW / 2 - 8, -stH / 2 + 8, isr);
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
        // text
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#ffffff";
        // Soft text shadow for readability
        ctx.shadowColor = "rgba(0,0,0,0.35)";
        ctx.shadowBlur = 4;
        ctx.shadowOffsetY = 2;
        ctx.fillText(stampText, 0, 2);
        ctx.restore();
      }

      // ============== Code highlight pill — premium / professional look ==============
      // Larger pill, layered gradient with a subtle top-gloss, refined
      // hierarchy: tiny line ornaments around the label, lighter tracking on
      // the code, and a soft shadow under the code for depth.
      const pillW = 760, pillH = 160;
      const pillX = (W - pillW) / 2;
      const pillY = qrCardY + qrCardSize + 55;

      // Pill outer shadow
      ctx.save();
      ctx.shadowColor = "rgba(33,196,110,0.45)";
      ctx.shadowBlur = 32;
      ctx.shadowOffsetY = 10;
      ctx.fillStyle = "#21c46e";
      const pr = 28;
      const px = pillX, py = pillY, pw = pillW, ph = pillH;
      ctx.beginPath();
      ctx.moveTo(px + pr, py);
      ctx.arcTo(px + pw, py,     px + pw, py + ph, pr);
      ctx.arcTo(px + pw, py + ph, px,     py + ph, pr);
      ctx.arcTo(px,     py + ph, px,     py,     pr);
      ctx.arcTo(px,     py,     px + pw, py,     pr);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Main gradient body
      const grad = ctx.createLinearGradient(pillX, pillY, pillX, pillY + pillH);
      grad.addColorStop(0, "#2ee084");
      grad.addColorStop(0.55, "#21c46e");
      grad.addColorStop(1, "#147544");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(pillX + pr, pillY);
      ctx.arcTo(pillX + pillW, pillY,         pillX + pillW, pillY + pillH, pr);
      ctx.arcTo(pillX + pillW, pillY + pillH, pillX,         pillY + pillH, pr);
      ctx.arcTo(pillX,         pillY + pillH, pillX,         pillY,         pr);
      ctx.arcTo(pillX,         pillY,         pillX + pillW, pillY,         pr);
      ctx.closePath();
      ctx.fill();

      // Top-half gloss highlight (subtle)
      const gloss = ctx.createLinearGradient(pillX, pillY, pillX, pillY + pillH / 2);
      gloss.addColorStop(0, "rgba(255,255,255,0.25)");
      gloss.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = gloss;
      ctx.beginPath();
      ctx.moveTo(pillX + pr, pillY);
      ctx.arcTo(pillX + pillW, pillY,         pillX + pillW, pillY + pillH, pr);
      ctx.lineTo(pillX + pillW, pillY + pillH / 2);
      ctx.lineTo(pillX,         pillY + pillH / 2);
      ctx.lineTo(pillX,         pillY);
      ctx.arcTo(pillX,         pillY,         pillX + pillW, pillY,         pr);
      ctx.closePath();
      ctx.fill();

      // Inner stroke for crispness
      ctx.strokeStyle = "rgba(0,0,0,0.18)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pillX + pr, pillY);
      ctx.arcTo(pillX + pillW, pillY,         pillX + pillW, pillY + pillH, pr);
      ctx.arcTo(pillX + pillW, pillY + pillH, pillX,         pillY + pillH, pr);
      ctx.arcTo(pillX,         pillY + pillH, pillX,         pillY,         pr);
      ctx.arcTo(pillX,         pillY,         pillX + pillW, pillY,         pr);
      ctx.closePath();
      ctx.stroke();

      // Label with thin decorative lines on either side: ─── REFERRAL CODE ───
      // Stronger weight, larger size, wider tracking → premium feel.
      const labelY = pillY + 50;
      const labelText = "REFERRAL CODE";
      // Draw with manual letter-spacing so we control the actual rendered width.
      ctx.font = "900 22px Helvetica, Arial, sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      const labelSpacing = 5; // px between chars
      let labelTotalW = 0;
      for (const ch of labelText) labelTotalW += ctx.measureText(ch).width + labelSpacing;
      labelTotalW -= labelSpacing;
      let lcx = W / 2 - labelTotalW / 2;
      for (const ch of labelText) {
        ctx.fillText(ch, lcx, labelY);
        lcx += ctx.measureText(ch).width + labelSpacing;
      }
      // thicker, longer decorative lines either side
      ctx.strokeStyle = "rgba(255,255,255,0.65)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      const lineGap = 18;
      const lineMidY = labelY - 7;
      ctx.beginPath();
      ctx.moveTo(pillX + 40, lineMidY);
      ctx.lineTo(W / 2 - labelTotalW / 2 - lineGap, lineMidY);
      ctx.moveTo(W / 2 + labelTotalW / 2 + lineGap, lineMidY);
      ctx.lineTo(pillX + pillW - 40, lineMidY);
      ctx.stroke();

      // Code itself — visually grounded, vertically centered, with depth
      const codeY = pillY + 130;
      ctx.font = "900 68px 'Arial Black', Helvetica, sans-serif";
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.32)";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 3;
      ctx.fillStyle = "#0a1119";
      // Add letter-spacing manually for premium feel
      const spacing = 5; // px
      let totalW = 0;
      for (const ch of currentCode) totalW += ctx.measureText(ch).width + spacing;
      totalW -= spacing;
      let cx = W / 2 - totalW / 2;
      ctx.textAlign = "left";
      for (const ch of currentCode) {
        ctx.fillText(ch, cx, codeY);
        cx += ctx.measureText(ch).width + spacing;
      }
      ctx.restore();

      // Username + invite URL footer — explicit Y coordinates so they never
      // collide with the bottom brand bar.
      const handle = username ? `@${username}` : "";
      const handleY = pillY + pillH + 75;        // ~75 px below the pill
      const urlY    = handleY + (handle ? 55 : 0); // 55 px below handle (or right under pill)
      if (handle) {
        ctx.font = "700 28px Helvetica, Arial, sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.65)";
        ctx.textAlign = "center";
        ctx.fillText(`Invited by ${handle}`, W / 2, handleY);
      }
      let urlText = (liveShareUrl || "").replace(/^https?:\/\//, "");
      if (urlText.length > 46) urlText = urlText.slice(0, 44) + "…";
      ctx.font = "600 24px 'Menlo', 'Courier New', monospace";
      ctx.fillStyle = "rgba(255,201,51,0.9)";
      ctx.textAlign = "center";
      ctx.fillText(urlText, W / 2, handle ? urlY : handleY);

      // Bottom brand bar — sits well below the URL because H is now 1500.
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.fillRect(0, H - 80, W, 80);
      ctx.font = "700 24px Helvetica, Arial, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.textAlign = "center";
      ctx.fillText("Play Smart  ·  Win Big  ·  maxlive88.com", W / 2, H - 30);

      // Export → Blob → open in preview modal. We deliberately avoid the
      // programmatic `<a download>` trick after an await chain because iOS
      // Safari + most in-app WebViews silently drop downloads when the user
      // gesture stack has been broken by `await`. Instead we render the image
      // into a modal where the user can: (a) tap the native share-sheet via
      // Web Share API, (b) long-press to save, or (c) press a Save button.
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error("canvas export failed")), "image/png", 0.95);
      });
      const url = URL.createObjectURL(blob);
      const file = new File([blob], `maxlive88-share-${currentCode}.png`, { type: "image/png" });
      setPreview({ url, blob, file, kind: "card" });
    } catch (e) {
      toast.error(e?.message || "Could not generate card");
    } finally {
      setExporting(false);
    }
  };

  // Maxlive88 brand badge — inline SVG (no network), rendered at QR center.
  // "M" white + "88" brand-red over a rounded dark square; readable when scaled.
  // Built as a data URI so qrcode.react can embed it via imageSettings.
  const brandLogoDataUri = (() => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect x="0" y="0" width="64" height="64" rx="13" fill="#ffffff"/><rect x="4" y="4" width="56" height="56" rx="10" fill="#0a1119"/><text x="32" y="30" text-anchor="middle" font-family="Arial Black,Helvetica,sans-serif" font-weight="900" font-size="22" fill="#ffffff" letter-spacing="-1">M</text><text x="32" y="52" text-anchor="middle" font-family="Arial Black,Helvetica,sans-serif" font-weight="900" font-size="22" fill="#ff2740" letter-spacing="-1">88</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  })();

  // Visual state for the input ring + status pill
  const ringColor =
    status === "available" ? "#22c55e" :
    status === "taken" || status === "invalid" || status === "cooldown" ? "#ef4444" :
    status === "own" ? "#94a3b8" :
    status === "checking" ? "#ffc933" : "rgba(255,255,255,0.12)";

  return (
    <div
      className="rounded-2xl p-5 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(124,58,237,0.10) 0%, rgba(10,17,25,0.7) 60%, #0a1119 100%)",
        border: "1px solid rgba(167,139,250,0.35)",
      }}
      data-testid="custom-refer-code-card"
    >
      <div className="flex items-center gap-2 text-white font-bold text-[14px]">
        <span
          className="h-7 w-7 rounded-lg flex items-center justify-center"
          style={{ background: "rgba(167,139,250,0.14)", border: "1px solid rgba(167,139,250,0.45)" }}
        >
          <Edit3 size={14} style={{ color: "#a78bfa" }} />
        </span>
        Customize your code
        <span className="ml-auto text-[9px] uppercase tracking-[1.5px] font-bold text-purple-200/80 bg-purple-500/15 border border-purple-400/30 rounded-full px-2 py-0.5">
          Personal channel only
        </span>
      </div>
      <div className="text-white/55 text-[12px] mt-1 leading-relaxed">
        Pick a unique referral code — easier to remember and share. Separate from your Affiliate &amp; Promo codes.
      </div>

      {/* Cooldown banner — shown only when next change is locked */}
      {locked && (
        <div
          className="mt-3 rounded-xl px-3 py-2.5 flex items-start gap-2"
          style={{
            background: "rgba(251,191,36,0.08)",
            border: "1px solid rgba(251,191,36,0.32)",
          }}
          data-testid="custom-code-cooldown-banner"
        >
          <AlertCircle size={14} className="text-amber-300 shrink-0 mt-[1px]" />
          <div className="text-[12px] leading-snug">
            <div className="text-amber-200 font-bold">
              Locked for {daysRemaining} more day{daysRemaining === 1 ? "" : "s"}
            </div>
            <div className="text-amber-100/70 mt-0.5">
              You can change your custom code once every 90 days
              {cooldown?.next_change_at ? ` · next change on ${new Date(cooldown.next_change_at).toLocaleDateString()}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Input + live status */}
      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-[1.5px] font-bold text-white/45">
          New referral code
        </label>
        <div
          className="mt-1.5 h-12 rounded-xl flex items-center gap-2 px-3 transition-all"
          style={{
            background: "#0e1620",
            border: `1px solid ${ringColor}`,
            boxShadow: status === "available" ? "0 0 0 3px rgba(34,197,94,0.12)" :
                       (status === "taken" || status === "invalid" || status === "cooldown") ? "0 0 0 3px rgba(239,68,68,0.10)" : "none",
            opacity: locked ? 0.55 : 1,
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value.replace(/\s+/g, ""))}
            placeholder={locked ? "Code change locked" : "e.g. RAHIM2026"}
            maxLength={20}
            disabled={locked}
            className="flex-1 bg-transparent outline-none text-white font-bold tracking-[0.18em] text-[15px] uppercase placeholder:text-white/25 placeholder:tracking-normal placeholder:font-normal disabled:cursor-not-allowed"
            data-testid="custom-code-input"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
          />
          {input && !locked && (
            <button
              type="button"
              onClick={() => { setInput(""); setStatus("idle"); setReason(""); }}
              className="h-7 w-7 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition"
              data-testid="custom-code-clear"
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 min-h-[18px]">
          <StatusPill status={status} reason={reason} show={!!input} />
          <span className="text-[10px] text-white/30">
            6–20 chars · letters, numbers, _ or -
          </span>
        </div>
      </div>

      {/* Live preview of new link */}
      {input && status === "available" && (
        <div
          className="mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2"
          style={{ background: "rgba(34,197,94,0.08)", border: "1px dashed rgba(34,197,94,0.45)" }}
          data-testid="custom-code-preview"
        >
          <Link2 size={13} className="text-emerald-300 shrink-0" />
          <span className="text-emerald-100 text-[12px] truncate font-mono">{previewUrl}</span>
        </div>
      )}

      {/* Save button */}
      <button
        type="button"
        disabled={status !== "available" || saving || locked}
        onClick={onSave}
        className="w-full h-12 mt-3 rounded-xl font-extrabold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: (status === "available" && !locked)
            ? "linear-gradient(180deg, #a78bfa 0%, #8b5cf6 55%, #7c3aed 100%)"
            : "rgba(255,255,255,0.04)",
          color: (status === "available" && !locked) ? "#0a1119" : "rgba(255,255,255,0.5)",
          boxShadow: (status === "available" && !locked) ? "0 6px 18px rgba(124,58,237,0.30), inset 0 1px 0 rgba(255,255,255,0.3)" : "none",
          border: (status === "available" && !locked) ? "none" : "1px solid rgba(255,255,255,0.06)",
        }}
        data-testid="custom-code-save"
      >
        {locked ? (
          <><AlertCircle size={16} /> Locked · {daysRemaining}d remaining</>
        ) : saving ? (
          <><Loader2 size={16} className="animate-spin" /> Saving…</>
        ) : (
          <><Sparkles size={16} /> Generate &amp; activate</>
        )}
      </button>

      {/* ============== Professional Share Card (always visible) ============== */}
      <div
        className="mt-5 rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg,#0f1828 0%,#0a1119 60%,#080d14 100%)",
          border: "1px solid rgba(167,139,250,0.32)",
          boxShadow: "0 12px 40px -16px rgba(124,58,237,0.45)",
        }}
        data-testid="custom-code-share-card"
      >
        {/* Brand strip */}
        <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-2 border-b border-white/[0.06]">
          <div className="flex items-baseline gap-1 font-black text-[18px] leading-none">
            <span className="text-white">Maxlive</span>
            <span className="text-[#ff2740]">88</span>
          </div>
          <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[1.7px] bg-[#ffc933]/15 text-[#ffc933] border border-[#ffc933]/35">
            <Sparkles size={10} /> Invite & Earn
          </span>
        </div>

        {/* QR (centered, large, branded) */}
        <div className="px-5 pt-5 flex flex-col items-center">
          <div
            ref={qrRef}
            className="p-4 rounded-2xl bg-white relative"
            style={{
              boxShadow: "0 8px 28px rgba(0,0,0,0.5), 0 0 0 4px rgba(167,139,250,0.18), 0 0 0 8px rgba(167,139,250,0.08)",
            }}
            data-testid="custom-code-qr-svg"
          >
            {/* BONUS sticker — admin-controlled, sits OUTSIDE the QR card so it
                never obscures QR positioning markers / scan area */}
            {shareBonusText && (
              <div
                className="absolute z-10 select-none pointer-events-none"
                style={{
                  top: -28,
                  right: -34,
                  transform: "rotate(-12deg)",
                  filter: "drop-shadow(0 6px 14px rgba(255,39,64,0.5))",
                }}
                data-testid="custom-code-bonus-sticker"
              >
                <div
                  className="relative px-3 py-1.5 rounded-xl text-white font-black text-[12px] tracking-wide whitespace-nowrap"
                  style={{ background: "#ff2740" }}
                >
                  <span
                    aria-hidden
                    className="absolute inset-1 rounded-lg pointer-events-none"
                    style={{ border: "2px dashed rgba(255,255,255,0.7)" }}
                  />
                  <span className="relative" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.35)" }}>
                    {shareBonusText}
                  </span>
                </div>
              </div>
            )}
            <QRCodeSVG
              value={liveShareUrl || `${base}register`}
              size={208}
              level="H"
              bgColor="#ffffff"
              fgColor="#0a1119"
              includeMargin={false}
              imageSettings={{
                src: brandLogoDataUri,
                height: 48,
                width: 48,
                excavate: true,
              }}
            />
            {/* corner accents on the white card */}
            {["tl","tr","bl","br"].map((c) => (
              <span
                key={c}
                aria-hidden
                className="absolute h-3 w-3 border-[#21c46e]"
                style={{
                  borderTopWidth: c.includes("t") ? 3 : 0,
                  borderBottomWidth: c.includes("b") ? 3 : 0,
                  borderLeftWidth: c.includes("l") ? 3 : 0,
                  borderRightWidth: c.includes("r") ? 3 : 0,
                  top: c.includes("t") ? -2 : "auto",
                  bottom: c.includes("b") ? -2 : "auto",
                  left: c.includes("l") ? -2 : "auto",
                  right: c.includes("r") ? -2 : "auto",
                  borderRadius: 2,
                }}
              />
            ))}
          </div>
          <div className="mt-3 text-[10.5px] uppercase tracking-[2px] font-bold text-white/40">
            Scan to join with my code
          </div>
        </div>

        {/* Code highlight pill — premium */}
        <div className="px-5 mt-4">
          <div
            className="relative rounded-2xl px-4 py-3.5 flex items-center justify-between gap-3 overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #2ee084 0%, #21c46e 55%, #147544 100%)",
              border: "1px solid rgba(0,0,0,0.2)",
              boxShadow: "0 10px 26px -6px rgba(33,196,110,0.45), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.18)",
            }}
          >
            {/* top gloss */}
            <span
              aria-hidden
              className="absolute inset-x-0 top-0 h-1/2 pointer-events-none"
              style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 100%)" }}
            />
            <div className="min-w-0 relative">
              <div className="flex items-center gap-2">
                <span className="h-px w-4 bg-white/55" aria-hidden />
                <span className="text-[9.5px] uppercase tracking-[2.4px] font-bold text-white/85">
                  Referral code
                </span>
                <span className="h-px flex-1 bg-white/30" aria-hidden />
              </div>
              <div
                className="text-[#0a1119] font-black text-[24px] tracking-[0.18em] mt-1 truncate"
                style={{ textShadow: "0 1px 0 rgba(255,255,255,0.25)" }}
                data-testid="custom-code-pill-code"
              >
                {currentCode || "—"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => copyToClipboard(currentCode).then((ok) => ok ? toast.success("Code copied") : toast.error("Could not copy"))}
              className="shrink-0 h-10 w-10 rounded-lg inline-flex items-center justify-center transition active:scale-95 relative"
              style={{
                background: "rgba(10,17,25,0.18)",
                border: "1px solid rgba(10,17,25,0.28)",
                color: "#0a1119",
              }}
              data-testid="custom-code-pill-copy"
              aria-label="Copy code"
            >
              <Copy size={14} />
            </button>
          </div>
          {/* Link row */}
          <div
            className="mt-2 rounded-lg px-3 h-11 flex items-center gap-2 bg-[#0e1620] border border-white/8"
            data-testid="custom-code-link-row"
          >
            <Link2 size={13} className="text-white/40 shrink-0" />
            <span className="text-white/75 text-[12px] truncate font-mono flex-1 min-w-0">
              {liveShareUrl || "—"}
            </span>
            <button
              type="button"
              onClick={onCopyLiveLink}
              className="h-7 px-2 rounded-md bg-white/5 hover:bg-white/10 text-white/80 text-[11px] font-bold inline-flex items-center gap-1 transition active:scale-95"
              data-testid="custom-code-copy-live-link"
            >
              <Copy size={11} /> Copy
            </button>
          </div>
        </div>

        {/* Action grid — QR-only PNG / Share-card PNG / PDF */}
        <div className="px-5 pt-3 pb-5 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={onDownloadQR}
            className="h-11 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 transition active:scale-95"
            data-testid="custom-code-download-qr"
          >
            <QrCode size={13} /> QR only
          </button>
          <button
            type="button"
            onClick={onDownloadShareCard}
            disabled={exporting}
            className="h-11 rounded-lg font-extrabold inline-flex items-center justify-center gap-1.5 transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(180deg, #ffc933 0%, #f5a623 100%)",
              color: "#0a1119",
              boxShadow: "0 6px 16px rgba(255,201,51,0.25), inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
            data-testid="custom-code-download-card"
          >
            {exporting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <><ImageDown size={13} /> Card</>
            )}
          </button>
          <button
            type="button"
            onClick={onDownloadPDF}
            disabled={exporting}
            className="h-11 rounded-lg font-extrabold inline-flex items-center justify-center gap-1.5 transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(180deg, #ff2740 0%, #c41024 100%)",
              color: "#ffffff",
              boxShadow: "0 6px 16px rgba(255,39,64,0.30), inset 0 1px 0 rgba(255,255,255,0.3)",
            }}
            data-testid="custom-code-download-pdf"
          >
            {exporting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <><FileText size={13} /> PDF</>
            )}
          </button>
        </div>

        {/* Footer note */}
        <div className="px-5 pb-4 -mt-1 text-[10.5px] text-white/35 text-center leading-snug">
          Share the card on WhatsApp, Telegram, Instagram or anywhere — friends just scan to land on signup with your code.
        </div>
      </div>

      {/* ============== Preview / Save Modal — reliable on iOS + WebViews ============== */}
      {preview && (
        <SharePreviewModal
          file={preview.file}
          url={preview.url}
          downloadUrl={preview.downloadUrl}
          kind={preview.kind}
          code={currentCode}
          shareUrl={liveShareUrl}
          onClose={() => {
            try { URL.revokeObjectURL(preview.url); } catch { /* noop */ }
            try { if (preview.downloadUrl) URL.revokeObjectURL(preview.downloadUrl); } catch { /* noop */ }
            setPreview(null);
          }}
        />
      )}
    </div>
  );
}

function StatusPill({ status, reason, show }) {
  if (!show) return null;
  if (status === "checking")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300" data-testid="custom-code-status">
        <Loader2 size={11} className="animate-spin" /> Checking…
      </span>
    );
  if (status === "available")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300" data-testid="custom-code-status">
        <Check size={11} /> Available
      </span>
    );
  if (status === "own")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-white/50" data-testid="custom-code-status">
        <AlertCircle size={11} /> {reason || "Already your code"}
      </span>
    );
  if (status === "cooldown")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300" data-testid="custom-code-status">
        <AlertCircle size={11} /> {reason || "Cooldown active"}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-300" data-testid="custom-code-status">
      <AlertCircle size={11} /> {reason || "Not available"}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  SharePreviewModal — shown after the share-card / QR PNG is generated.     */
/*  Reliably works on iOS Safari + in-app WebViews where programmatic         */
/*  `<a download>` is silently blocked. Provides:                             */
/*    • Native share-sheet via Web Share API (when files are supported)       */
/*    • A direct "Save image" button (works on desktop + most mobile)         */
/*    • Visible image so the user can long-press → "Save image" as fallback   */
/* -------------------------------------------------------------------------- */
function SharePreviewModal({ file, url, downloadUrl, kind, code, shareUrl, onClose }) {
  // The URL used for the visible preview vs. the URL used for save/share can
  // differ — e.g. PDF kind uses a JPEG thumbnail for preview but downloads
  // the real PDF blob. When `downloadUrl` is omitted, both fall back to `url`.
  const saveUrl = downloadUrl || url;
  const canShareFile = (() => {
    try {
      return typeof navigator !== "undefined"
        && typeof navigator.canShare === "function"
        && navigator.canShare({ files: [file] });
    } catch {
      return false;
    }
  })();

  const onNativeShare = async () => {
    try {
      await navigator.share({
        title: "Join me on Maxlive88",
        text: `Sign up with my code ${code} — 100% welcome bonus.`,
        files: [file],
      });
    } catch (e) {
      if (e && e.name !== "AbortError") {
        toast.error("Share failed — try Save image instead");
      }
    }
  };

  // Detect "in-app" / restricted WebViews where `<a download>` silently
  // reports success but never actually writes a file (Telegram, FB Messenger,
  // Instagram, TikTok). For these, we deliberately use Web Share API or
  // open-in-new-tab instead.
  const isInAppBrowser = (() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /FB_IAB|FBAV|Instagram|Line|MicroMessenger|TikTok|Twitter|Telegram|TGWeb|WebView/i.test(ua);
  })();

  // Robust save handler — used as the anchor's `onClick`. The anchor itself
  // has `href={saveUrl}` + `download={file.name}` + `target="_blank"`, so the
  // native browser behavior is the PRIMARY save path:
  //   • Desktop browsers → the `download` attribute saves the file directly.
  //   • iOS Safari / Mobile → opens the blob in a new tab where the user can
  //     long-press → Save image, or use the Share menu → Save to Files.
  //   • Iframe-embedded previews (Emergent dashboard) → `target="_blank"`
  //     escapes the sandbox and the new tab renders the blob inline.
  //
  // We DO NOT call `e.preventDefault()` here, so the native anchor click
  // ALWAYS proceeds. The handler just shows a confirmation toast.
  const onSave = () => {
    setTimeout(() => {
      toast.success(kind === "pdf"
        ? "PDF opened — tap browser menu → Save / Download if it doesn't save automatically"
        : "Image opened — long-press / right-click → Save image");
    }, 60);
  };

  const onCopyLink = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) toast.success("Link copied"); else toast.error("Could not copy");
  };

  // ---- Deep-link share helpers ----
  // NOTE: Both WhatsApp and Telegram deep-links share TEXT/URL only — they
  // cannot attach the rendered PNG. So we open the chat with a pre-filled
  // invite message containing the share URL, and the user can additionally
  // send the saved image as an attachment from inside the chat.
  const inviteText = `Join me on Maxlive88 and get a 100% welcome bonus!\nUse my code: ${code}\n${shareUrl}`;

  const onShareWhatsApp = () => {
    const u = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;
    try { window.open(u, "_blank", "noopener,noreferrer"); }
    catch { window.location.href = u; }
  };

  const onShareTelegram = () => {
    const u = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Join me on Maxlive88 — use my code ${code} for a 100% welcome bonus!`)}`;
    try { window.open(u, "_blank", "noopener,noreferrer"); }
    catch { window.location.href = u; }
  };

  return (
    <div
      className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="share-preview-modal"
    >
      <div
        className="w-full sm:max-w-[420px] rounded-t-3xl sm:rounded-2xl overflow-hidden max-h-[94vh] flex flex-col"
        style={{
          background: "linear-gradient(180deg, #0d1727 0%, #0a1119 60%, #080d14 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: "0 -28px 60px -20px rgba(0,0,0,0.7)",
        }}
      >
        {/* drag handle (mobile) */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-white/15" />
        </div>

        {/* HEADER — brand-aware with the code badge */}
        <div className="px-5 pt-3 pb-4 border-b border-white/[0.05] flex items-start gap-3 shrink-0">
          <span
            className="h-10 w-10 rounded-xl inline-flex items-center justify-center shrink-0"
            style={{
              background: kind === "pdf"
                ? "linear-gradient(135deg, rgba(255,39,64,0.18) 0%, rgba(255,39,64,0.04) 100%)"
                : "linear-gradient(135deg, rgba(33,196,110,0.18) 0%, rgba(33,196,110,0.04) 100%)",
              border: kind === "pdf"
                ? "1px solid rgba(255,39,64,0.4)"
                : "1px solid rgba(33,196,110,0.4)",
            }}
          >
            {kind === "card" ? (
              <Sparkles size={16} className="text-emerald-300" />
            ) : kind === "pdf" ? (
              <FileText size={16} className="text-rose-300" />
            ) : (
              <QrCode size={16} className="text-emerald-300" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-extrabold text-[15px] leading-tight">
              {kind === "card" ? "Share card ready" : kind === "pdf" ? "PDF flyer ready" : "QR code ready"}
            </h3>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-[9.5px] uppercase tracking-[1.5px] font-bold text-white/40">Code</span>
              <span className="text-emerald-300 font-black text-[13px] tracking-[0.15em] truncate">
                {code}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] transition shrink-0"
            data-testid="share-preview-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* IMAGE / PDF PREVIEW */}
        <div className="px-4 pt-4 pb-2 overflow-y-auto flex-1">
          <div
            className="relative rounded-xl overflow-hidden bg-black/40 mx-auto"
            style={{
              border: kind === "pdf"
                ? "1px solid rgba(255,39,64,0.25)"
                : "1px solid rgba(167,139,250,0.25)",
              boxShadow: kind === "pdf"
                ? "0 8px 24px -8px rgba(255,39,64,0.35)"
                : "0 8px 24px -8px rgba(124,58,237,0.35)",
              maxWidth: 340,
            }}
          >
            {kind === "pdf" ? (
              // Show the JPEG thumbnail (NOT an iframe — Chrome blocks blob: PDF iframes).
              // The actual PDF blob is downloaded by the Save button via `downloadUrl`.
              <img
                src={url}
                alt="Maxlive88 referral PDF preview"
                className="block w-full h-auto"
                style={{ background: "#fff" }}
                data-testid="share-preview-pdf"
              />
            ) : (
              <img
                src={url}
                alt="Maxlive88 share"
                className="block w-full h-auto"
                draggable
                data-testid="share-preview-image"
              />
            )}
            {/* Floating tap-to-save hint badge */}
            <div
              className="absolute left-1/2 -translate-x-1/2 bottom-3 px-3 py-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center gap-1.5 pointer-events-none"
              style={{
                background: "rgba(10,17,25,0.85)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
                backdropFilter: "blur(6px)",
              }}
            >
              <span className="inline-block animate-pulse">👇</span>
              {kind === "pdf" ? " Tap Save PDF below" : " Tap Save image below"}
            </div>
          </div>
          <div className="text-[11px] text-emerald-200/85 mt-3 text-center leading-snug px-2">
            {kind === "pdf" ? (
              <>
                <b className="text-white">Tap Save PDF</b> below to download — perfect for email, print, or formal sharing.
              </>
            ) : (
              <>
                <b className="text-white">Tap Save image</b> below to download to your device — or long-press the image to copy / save manually.
              </>
            )}
          </div>
        </div>

        {/* QUICK-SHARE ROW — WhatsApp + Telegram deep links */}
        <div className="px-4 pt-2 pb-2 shrink-0">
          <div className="text-[9.5px] uppercase tracking-[1.7px] font-bold text-white/35 mb-2 px-1">
            Quick share
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onShareWhatsApp}
              className="h-12 rounded-xl font-extrabold inline-flex items-center justify-center gap-2 transition active:scale-95"
              style={{
                background: "linear-gradient(180deg, #25d366 0%, #128c7e 100%)",
                color: "#06120a",
                boxShadow: "0 6px 16px rgba(37,211,102,0.32), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
              data-testid="share-preview-whatsapp"
            >
              <MessageCircle size={15} fill="currentColor" /> WhatsApp
            </button>
            <button
              type="button"
              onClick={onShareTelegram}
              className="h-12 rounded-xl font-extrabold inline-flex items-center justify-center gap-2 transition active:scale-95"
              style={{
                background: "linear-gradient(180deg, #2aabee 0%, #1c8ac4 100%)",
                color: "#021b29",
                boxShadow: "0 6px 16px rgba(42,171,238,0.32), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
              data-testid="share-preview-telegram"
            >
              <Send size={14} fill="currentColor" /> Telegram
            </button>
          </div>
        </div>

        {/* SECONDARY ACTIONS — Save + (Native Share OR Copy link) */}
        <div className="px-4 pt-2 pb-4 shrink-0">
          <div className="grid grid-cols-2 gap-2">
            {canShareFile ? (
              <button
                type="button"
                onClick={onNativeShare}
                className="h-11 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-2 transition active:scale-95"
                data-testid="share-preview-native"
              >
                <Share2 size={13} /> More apps
              </button>
            ) : (
              <button
                type="button"
                onClick={onCopyLink}
                className="h-11 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-2 transition active:scale-95"
                data-testid="share-preview-copy-link"
              >
                <Copy size={13} /> Copy link
              </button>
            )}
            <a
              href={saveUrl}
              download={file.name}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onSave}
              className="h-11 rounded-lg font-extrabold inline-flex items-center justify-center gap-2 transition active:scale-95 cursor-pointer no-underline"
              style={{
                background: "linear-gradient(180deg, #ffc933 0%, #f5a623 100%)",
                color: "#0a1119",
                boxShadow: "0 6px 16px rgba(255,201,51,0.30), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
              data-testid="share-preview-save"
            >
              <Download size={13} /> {kind === "pdf" ? "Save PDF" : "Save image"}
            </a>
          </div>
        </div>
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}




function CodeCard({
  title, subtitle, code, shareUrl, accent, accentSoft, accentBorder,
  icon: Icon, onCopyCode, onCopyLink, onShare, copyKey, copied, ribbon, testidPrefix,
}) {
  return (
    <div
      className="relative rounded-2xl p-5 overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${accentSoft} 0%, rgba(10,17,25,0.7) 60%, #0a1119 100%)`,
        border: `1px solid ${accentBorder}`,
      }}
      data-testid={`${testidPrefix}-card`}
    >
      <div
        aria-hidden
        className="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-30"
        style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
      />

      {ribbon && (
        <div
          className="absolute right-0 top-5 px-3 h-6 flex items-center text-[10px] font-extrabold uppercase tracking-widest text-white"
          style={{ background: accent, boxShadow: `0 2px 10px ${accentSoft}` }}
        >
          {ribbon}
        </div>
      )}

      <div className="relative flex items-center gap-2 text-white font-bold text-[14px]">
        <span
          className="h-7 w-7 rounded-lg flex items-center justify-center"
          style={{ background: accentSoft, border: `1px solid ${accentBorder}` }}
        >
          <Icon size={14} style={{ color: accent }} />
        </span>
        {title}
      </div>
      <div className="text-white/50 text-[12px] mt-1 leading-relaxed">{subtitle}</div>

      {/* Code tile */}
      <div className="relative mt-4">
        <div
          data-testid={`${testidPrefix}-code`}
          className="h-14 rounded-xl flex items-center justify-center text-white font-black tracking-[0.22em] text-[20px] relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg,#1a2030 0%,#0e1620 100%)",
            border: `1px dashed ${accentBorder}`,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 4px ${accentSoft}`,
          }}
        >
          {code || "—"}
          <button
            type="button"
            onClick={onCopyCode}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-lg flex items-center justify-center transition-all active:scale-90"
            style={{ background: "rgba(255,255,255,0.06)", color: "white" }}
            data-testid={`${testidPrefix}-copy-code`}
          >
            {copied === `${copyKey}-code` ? <Check size={15} style={{ color: accent }} /> : <Copy size={15} />}
          </button>
        </div>
      </div>

      {/* Link row */}
      <div className="relative mt-2 flex items-center gap-2">
        <div
          className="flex-1 h-11 rounded-lg bg-[#0e1620] border border-white/10 px-3 flex items-center gap-2 min-w-0"
          data-testid={`${testidPrefix}-url`}
        >
          <Link2 size={13} className="text-white/40 shrink-0" />
          <span className="text-white/75 text-[12px] truncate">{shareUrl || "—"}</span>
        </div>
        <button
          type="button"
          onClick={onCopyLink}
          className="h-11 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-white text-[12px] font-semibold inline-flex items-center gap-1 transition-all active:scale-95"
          data-testid={`${testidPrefix}-copy-link`}
        >
          {copied === `${copyKey}-link` ? <><Check size={13} style={{ color: accent }} /> Copied</> : <><Copy size={13} /> Copy</>}
        </button>
      </div>

      {/* Share CTA */}
      <button
        type="button"
        onClick={onShare}
        className="relative w-full h-12 mt-3 rounded-xl text-[#0a1119] font-extrabold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
        style={{
          background: `linear-gradient(180deg, ${accent} 0%, ${accent}dd 55%, ${accent}aa 100%)`,
          boxShadow: `0 6px 18px ${accentSoft}, inset 0 1px 0 rgba(255,255,255,0.3)`,
        }}
        data-testid={`${testidPrefix}-share`}
      >
        <Share2 size={16} /> Share
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

function TinyStat({ label, value, icon: Icon, color }) {
  return (
    <div
      className="rounded-xl px-3 py-2.5"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/50 font-semibold">
        <Icon size={10} style={{ color }} /> {label}
      </div>
      <div className="text-white font-black text-[15px] tabular-nums mt-0.5 truncate" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function EarningStat({ label, value }) {
  return (
    <div className="rounded-lg bg-black/30 border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-wider text-white/45 font-semibold">{label}</div>
      <div className="text-white font-black text-lg tabular-nums mt-1">{value}</div>
    </div>
  );
}

function HowStep({ n, icon: Icon, title, text, color }) {
  return (
    <div className="rounded-xl p-3 bg-black/30 border border-white/5 relative">
      <div
        className="absolute -top-2 -right-2 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-black text-[#0a1119]"
        style={{ background: color }}
      >
        {n}
      </div>
      <div className="h-8 w-8 rounded-lg flex items-center justify-center mb-1.5" style={{ background: `${color}22` }}>
        <Icon size={15} style={{ color }} />
      </div>
      <div className="text-white text-[12px] font-bold">{title}</div>
      <div className="text-white/45 text-[10px] leading-snug mt-0.5">{text}</div>
    </div>
  );
}


function MiniCell({ icon: Icon, label, value, color, highlight }) {
  return (
    <div
      className="rounded-lg px-2 py-1.5 min-w-0"
      style={{
        background: highlight ? `${color}14` : "rgba(255,255,255,0.03)",
        border: `1px solid ${highlight ? `${color}55` : "rgba(255,255,255,0.06)"}`,
      }}
    >
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-white/45 font-semibold">
        <Icon size={9} style={{ color }} /> {label}
      </div>
      <div
        className="font-black text-[12px] tabular-nums mt-0.5 truncate"
        style={{ color: highlight ? color : "white" }}
      >
        {value}
      </div>
    </div>
  );
}


/* Tiny inline 7-day sparkline (CSS-only bar chart, no extra deps). */
function Last7Spark({ data, symbol }) {
  const max = Math.max(1, ...data.map((d) => Number(d.amount || 0)));
  return (
    <div className="mt-3" data-testid="ngr-spark-chart">
      <div className="flex items-end justify-between gap-1 h-12">
        {data.map((d) => {
          const v = Number(d.amount || 0);
          const h = Math.max(2, Math.round((v / max) * 100));
          const dim = v === 0;
          return (
            <div
              key={d.date}
              className="flex-1 rounded-t transition-all"
              title={`${d.date}: ${symbol}${v.toFixed(2)}`}
              style={{
                height: `${h}%`,
                background: dim ? "rgba(255,255,255,0.08)" : "linear-gradient(180deg,#22c55e,#15803d)",
                boxShadow: dim ? "none" : "0 0 8px rgba(34,197,94,0.4)",
              }}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[8.5px] text-white/30 font-mono uppercase tracking-[1px]">
        <span>{data[0]?.date?.slice(5) || ""}</span>
        <span>Last 7 days</span>
        <span>{data[data.length - 1]?.date?.slice(5) || ""}</span>
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  InviteesList — advanced referral table with:                              */
/*    • Search by username                                                    */
/*    • Sort: recent / deposit / withdraw / commission / loss                 */
/*    • Per-row site + agent split for both deposits & withdrawals            */
/*    • Per-referee lifetime NGR commission earned (the 10% credited)         */
/*    • Click row to expand and see full breakdown                            */
/*    • Server-side pagination via /profile/referral/invitees                 */
/* -------------------------------------------------------------------------- */
function InviteesList({ invitees: initialInvitees, invitedTotal, symbol, ngrPct }) {
  const PAGE = 12;
  const [items, setItems] = useState(initialInvitees || []);
  const [total, setTotal] = useState(invitedTotal || 0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [sparkDays, setSparkDays] = useState(7); // 7 | 15 | 30
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const debounceRef = useRef(null);

  // Compute aggregate stats on currently-loaded list (lifetime view)
  const totals = (items || []).reduce(
    (acc, u) => {
      acc.site_dep += Number(u.site_deposit || 0);
      acc.agent_dep += Number(u.agent_deposit || 0);
      acc.site_wd += Number(u.site_withdraw || 0);
      acc.agent_wd += Number(u.agent_withdraw || 0);
      acc.ngr += Number(u.ngr_commission_earned || 0);
      acc.dep += Number(u.deposit_total || 0);
      acc.wd += Number(u.withdraw_total || 0);
      return acc;
    },
    { site_dep: 0, agent_dep: 0, site_wd: 0, agent_wd: 0, ngr: 0, dep: 0, wd: 0 }
  );

  const fetchPage = async (newSkip = 0, newQ = q, newSort = sort, newSpark = sparkDays, replace = true) => {
    setBusy(true);
    try {
      const { data } = await api.get("/profile/referral/invitees", {
        params: { limit: PAGE, skip: newSkip, q: newQ, sort: newSort, spark_days: newSpark },
      });
      setTotal(Number(data.total || 0));
      if (replace) setItems(data.items || []);
      else setItems((prev) => [...(prev || []), ...(data.items || [])]);
      setSkip(newSkip);
    } catch {
      // keep last good state
    } finally {
      setBusy(false);
    }
  };

  // Initial load — always pull from server so we have sparkline + canonical 12-row pagination
  useEffect(() => {
    fetchPage(0, "", "recent", sparkDays, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    if (!dirty) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPage(0, q, sort, sparkDays, true);
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const onChangeSort = (next) => {
    setDirty(true);
    setSort(next);
    fetchPage(0, q, next, sparkDays, true);
  };

  const onChangeSparkDays = (next) => {
    setSparkDays(next);
    fetchPage(0, q, sort, next, true);
  };

  const onLoadMore = () => {
    setDirty(true);
    fetchPage(skip + PAGE, q, sort, sparkDays, false);
  };

  const onDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/profile/referral/invitees/export.pdf", {
        params: { q, sort: sort === "recent" ? "deposit" : sort },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `referral-report-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF download failed. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const hasMore = items.length < total;

  const fmt = (v, dec = 0) =>
    `${symbol}${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: dec })}`;

  return (
    <div className="rounded-2xl bg-[#0f1622] border border-white/5 overflow-hidden" data-testid="invitees-list">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold">
          <Users size={12} /> Your invitees · {ngrPct.toFixed(0)}% NGR
        </div>
        <div className="text-[11px] text-white/35">
          {items.length}/{total}
        </div>
      </div>

      {/* Lifetime aggregate strip — only shown when there is some loaded activity */}
      {(totals.dep > 0 || totals.wd > 0 || totals.ngr > 0) && (
        <div
          className="px-4 py-3 grid grid-cols-3 gap-2 border-b border-white/5"
          style={{ background: "linear-gradient(135deg,#0d2418 0%,#0b121c 100%)" }}
          data-testid="invitees-aggregate"
        >
          <div className="rounded-lg p-2" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.22)" }}>
            <div className="text-[9px] uppercase tracking-wider text-white/45 font-semibold flex items-center gap-1">
              <ArrowDownToLine size={9} className="text-blue-400" /> Total Deposit
            </div>
            <div className="font-black text-[13px] text-white tabular-nums mt-0.5">{fmt(totals.dep)}</div>
            <div className="text-[9px] text-white/40 mt-0.5 leading-tight">Site + Agent · successful</div>
          </div>
          <div className="rounded-lg p-2" style={{ background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.22)" }}>
            <div className="text-[9px] uppercase tracking-wider text-white/45 font-semibold flex items-center gap-1">
              <ArrowUpFromLine size={9} className="text-purple-300" /> Total Withdraw
            </div>
            <div className="font-black text-[13px] text-white tabular-nums mt-0.5">{fmt(totals.wd)}</div>
            <div className="text-[9px] text-white/40 mt-0.5 leading-tight">Site + Agent · successful</div>
          </div>
          <div className="rounded-lg p-2" style={{ background: "rgba(255,201,51,0.10)", border: "1px solid rgba(255,201,51,0.30)" }}>
            <div className="text-[9px] uppercase tracking-wider text-white/45 font-semibold flex items-center gap-1">
              <Coins size={9} className="text-amber-300" /> NGR Earned
            </div>
            <div className="font-black text-[13px] text-amber-300 tabular-nums mt-0.5">{fmt(totals.ngr, 2)}</div>
            <div className="text-[9px] text-white/40 mt-0.5 leading-tight">From these invitees · lifetime</div>
          </div>
        </div>
      )}

      {/* Search + sort + spark-window + PDF download */}
      {(total > 0 || dirty) && (
        <div className="px-3 py-2.5 space-y-2 border-b border-white/5 bg-white/[0.015]">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                type="text"
                value={q}
                onChange={(e) => { setDirty(true); setQ(e.target.value); }}
                placeholder="Search username…"
                className="w-full h-9 pl-8 pr-3 rounded-lg bg-white/[0.04] border border-white/10 text-white text-[12.5px] placeholder:text-white/30 outline-none focus:border-amber-400/40 transition"
                data-testid="invitees-search"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => onChangeSort(e.target.value)}
              className="h-9 px-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-white text-[12px] outline-none focus:border-amber-400/40 transition"
              data-testid="invitees-sort"
            >
              <option value="recent">Newest</option>
              <option value="deposit">Top Deposit</option>
              <option value="withdraw">Top Withdraw</option>
              <option value="commission">Top NGR</option>
              <option value="loss">Top Loss</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            {/* Spark window toggle */}
            <div className="inline-flex rounded-lg overflow-hidden border border-white/10" data-testid="invitees-spark-toggle">
              {[7, 15, 30].map((d) => {
                const active = sparkDays === d;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => onChangeSparkDays(d)}
                    className={`px-2.5 h-8 text-[11px] font-bold transition ${
                      active
                        ? "bg-amber-400/20 text-amber-300"
                        : "bg-white/[0.03] text-white/55 hover:text-white/75"
                    }`}
                    data-testid={`invitees-spark-${d}`}
                  >
                    {d === 30 ? "30D" : `${d}D`}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-white/40 flex-1 truncate">
              NGR trend · last {sparkDays} days
            </div>
            {/* PDF download */}
            <button
              type="button"
              onClick={onDownloadPdf}
              disabled={pdfBusy || total === 0}
              className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg bg-red-500/15 border border-red-500/35 text-red-200 text-[11px] font-bold hover:bg-red-500/25 active:scale-[0.97] transition disabled:opacity-50"
              data-testid="invitees-pdf-download"
              title="Download full invitee list as PDF"
            >
              {pdfBusy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
              PDF
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 && !busy ? (
        <div className="p-8 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 mb-3">
            <QrCode size={24} className="text-white/30" />
          </div>
          <div className="text-white/70 text-sm font-semibold">
            {q ? "No match found" : "No invitees yet"}
          </div>
          <div className="text-white/40 text-xs mt-1">
            {q ? "Try a different username" : "Share your code above to start earning"}
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.map((u, idx) => (
            <InviteeRow
              key={u.user_id || idx}
              u={u}
              idx={idx}
              symbol={symbol}
              ngrPct={ngrPct}
              sparkDays={sparkDays}
              expanded={expandedId === (u.user_id || idx)}
              onToggle={() => setExpandedId(expandedId === (u.user_id || idx) ? null : (u.user_id || idx))}
            />
          ))}
        </ul>
      )}

      {/* Pagination — Load more */}
      {hasMore && (
        <div className="px-4 py-3 border-t border-white/5">
          <button
            onClick={onLoadMore}
            disabled={busy}
            className="w-full h-10 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-2 hover:bg-white/[0.07] transition active:scale-[0.985] disabled:opacity-50"
            data-testid="invitees-load-more"
          >
            {busy ? (
              <><Loader2 size={14} className="animate-spin" /> Loading…</>
            ) : (
              <><ChevronDown size={14} /> Load more · {total - items.length} left</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/* Single invitee row — collapsed = summary; expanded = full breakdown w/ NGR trend */
function InviteeRow({ u, idx, symbol, ngrPct, sparkDays, expanded, onToggle }) {
  const siteDep = Number(u.site_deposit || 0);
  const agentDep = Number(u.agent_deposit || 0);
  const siteWd = Number(u.site_withdraw || 0);
  const agentWd = Number(u.agent_withdraw || 0);
  const dep = Number(u.deposit_total || siteDep + agentDep);
  const wd = Number(u.withdraw_total || siteWd + agentWd);
  const loss = Number(u.net_loss_positive ?? Math.max(0, dep - wd));
  const ngrEarned = Number(u.ngr_commission_earned || 0);
  const spark = Array.isArray(u.ngr_spark) ? u.ngr_spark : [];
  const sparkSum = spark.reduce((a, b) => a + Number(b || 0), 0);
  const hasActivity = dep > 0 || wd > 0 || ngrEarned > 0;

  const fmt = (v, dec = 0) =>
    `${symbol}${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: dec })}`;

  return (
    <li className="px-4 py-3" data-testid={`invitee-${idx}`}>
      {/* Top row — avatar + name + joined + earn pill */}
      <button
        type="button"
        onClick={hasActivity ? onToggle : undefined}
        className={`w-full flex items-center gap-3 text-left ${hasActivity ? "cursor-pointer" : "cursor-default"}`}
        data-testid={`invitee-${idx}-toggle`}
      >
        <div
          className="h-9 w-9 rounded-full flex items-center justify-center font-black text-white text-xs shrink-0"
          style={{
            background: `linear-gradient(135deg, hsl(${(idx * 57) % 360} 65% 50%), hsl(${(idx * 57 + 30) % 360} 60% 30%))`,
          }}
        >
          {(u.username_masked || "U").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">{u.username_masked}</div>
          <div className="text-white/40 text-[11px]">{fmtWhen(u.joined_at)}</div>
        </div>
        {/* Inline sparkline — last `sparkDays` days NGR commission */}
        {spark.length > 1 && (
          <NgrSparkline
            data={spark}
            width={56}
            height={20}
            color={sparkSum > 0 ? "#fbbf24" : "rgba(255,255,255,0.18)"}
            title={`NGR last ${sparkDays}d · sum ${symbol}${sparkSum.toFixed(2)}`}
          />
        )}
        {ngrEarned > 0 ? (
          <div className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold tabular-nums shrink-0" title={`Lifetime NGR commission earned (${ngrPct.toFixed(0)}%)`}>
            <Coins size={10} /> +{fmt(ngrEarned, 2)}
          </div>
        ) : (
          <div className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider shrink-0">
            <Check size={10} /> Joined
          </div>
        )}
        {hasActivity && (
          expanded
            ? <ChevronUp size={14} className="text-white/40 shrink-0 ml-0.5" />
            : <ChevronDown size={14} className="text-white/40 shrink-0 ml-0.5" />
        )}
      </button>

      {/* Compact 3-cell summary (always visible when there's any activity) */}
      {hasActivity && (
        <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid={`invitee-${idx}-summary`}>
          <MiniCell
            icon={ArrowDownToLine}
            label="Deposit"
            value={fmt(dep)}
            color="#3b82f6"
          />
          <MiniCell
            icon={ArrowUpFromLine}
            label="Withdraw"
            value={fmt(wd)}
            color="#a78bfa"
          />
          <MiniCell
            icon={TrendingUp}
            label="Net Loss"
            value={fmt(loss)}
            color="#f97316"
          />
        </div>
      )}

      {/* Expanded breakdown — combined totals + NGR earned */}
      {expanded && hasActivity && (
        <div
          className="mt-2.5 rounded-xl p-2.5 space-y-2"
          style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}
          data-testid={`invitee-${idx}-breakdown`}
        >
          {/* Deposit + Withdraw — Site + Agent combined, successful only */}
          <div className="grid grid-cols-2 gap-1.5">
            <SplitCell icon={ArrowDownToLine} label="Deposit (Site + Agent)" value={fmt(dep)} color="#3b82f6" />
            <SplitCell icon={ArrowUpFromLine} label="Withdraw (Site + Agent)" value={fmt(wd)} color="#a78bfa" />
          </div>
          {/* Big sparkline — daily NGR commission window */}
          {spark.length > 1 && (
            <div
              className="rounded-lg p-2.5"
              style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.22)" }}
              data-testid={`invitee-${idx}-spark`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[9.5px] uppercase tracking-widest text-amber-300/85 font-bold flex items-center gap-1">
                  <TrendingUp size={10} /> Daily NGR · last {sparkDays} days
                </div>
                <div className="text-[10px] tabular-nums text-amber-200/80 font-bold">
                  Σ {fmt(sparkSum, 2)}
                </div>
              </div>
              <NgrSparkline
                data={spark}
                width={300}
                height={42}
                color="#fbbf24"
                fill="rgba(251,191,36,0.18)"
                showAxis
                title={`NGR per day, last ${sparkDays} days`}
              />
            </div>
          )}
          {/* NGR earned row — highlighted */}
          <div
            className="rounded-lg px-2.5 py-2 flex items-center justify-between"
            style={{
              background: "linear-gradient(90deg, rgba(255,201,51,0.10) 0%, rgba(255,201,51,0.04) 100%)",
              border: "1px solid rgba(255,201,51,0.30)",
            }}
          >
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full inline-flex items-center justify-center" style={{ background: "rgba(255,201,51,0.18)" }}>
                <Coins size={13} className="text-amber-300" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold">
                  NGR commission · {ngrPct.toFixed(0)}%
                </div>
                <div className="text-[10px] text-white/45 leading-tight">From this referee, lifetime</div>
              </div>
            </div>
            <div className="font-black text-amber-300 text-[15px] tabular-nums">
              +{fmt(ngrEarned, 2)}
            </div>
          </div>
          <div className="text-[9.5px] text-white/35 leading-tight">
            Only <span className="text-emerald-300 font-semibold">successful</span> deposits & withdrawals are counted (pending/cancelled excluded).
          </div>
        </div>
      )}
    </li>
  );
}

/* Small two-tone cell used inside expanded invitee breakdown */
function SplitCell({ icon: Icon, label, value, color }) {
  return (
    <div
      className="rounded-lg px-2 py-1.5 flex items-center gap-2"
      style={{ background: `${color}10`, border: `1px solid ${color}33` }}
    >
      <div className="h-6 w-6 rounded-md inline-flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
        <Icon size={11} style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">{label}</div>
        <div className="font-black text-[12.5px] tabular-nums text-white truncate">{value}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  NgrSparkline — compact SVG line+area chart for daily NGR commission       */
/*  data: number[]  (oldest → newest)                                         */
/* -------------------------------------------------------------------------- */
function NgrSparkline({ data, width = 56, height = 20, color = "#fbbf24", fill, showAxis = false, title }) {
  const arr = (data || []).map((v) => Number(v) || 0);
  if (arr.length < 2) {
    return (
      <svg width={width} height={height} aria-label={title} className="shrink-0">
        <line x1="0" y1={height / 2} x2={width} y2={height / 2}
              stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" strokeDasharray="2 2" />
      </svg>
    );
  }
  const min = 0;
  const max = Math.max(...arr, 1);
  const pad = 1.5;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const stepX = w / (arr.length - 1);
  const points = arr.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (h - ((v - min) / (max - min)) * h);
    return [x, y];
  });
  const path = points
    .map((p, i) => (i === 0 ? `M ${p[0].toFixed(1)} ${p[1].toFixed(1)}` : `L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`))
    .join(" ");
  const areaPath = `${path} L ${points[points.length - 1][0].toFixed(1)} ${height - pad} L ${points[0][0].toFixed(1)} ${height - pad} Z`;
  const last = points[points.length - 1];
  const lastVal = arr[arr.length - 1];

  return (
    <svg width={width} height={height} aria-label={title} className="shrink-0">
      <title>{title}</title>
      {showAxis && (
        <>
          <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad}
                stroke="rgba(255,255,255,0.08)" strokeWidth="0.8" />
          <line x1={pad} y1={pad} x2={pad} y2={height - pad}
                stroke="rgba(255,255,255,0.06)" strokeWidth="0.8" />
        </>
      )}
      {fill && <path d={areaPath} fill={fill} stroke="none" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.4"
            strokeLinejoin="round" strokeLinecap="round" />
      {/* Last-day dot */}
      <circle cx={last[0]} cy={last[1]} r={lastVal > 0 ? 1.8 : 1.2}
              fill={lastVal > 0 ? color : "rgba(255,255,255,0.35)"} />
    </svg>
  );
}


/* Small PDF download button — fetches the NGR Commission overview as a PDF.
   Lives on the green "NGR commission · 10%" card header. */
function NgrOverviewPdfButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await api.get("/profile/referral/ngr-overview/export.pdf", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `ngr-overview-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert("PDF download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title="Download NGR commission overview as PDF"
      data-testid="ngr-overview-pdf"
      className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[9.5px] uppercase tracking-[1.2px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/35 hover:bg-amber-500/25 active:scale-[0.97] transition disabled:opacity-50"
    >
      {busy ? <Loader2 size={10} className="animate-spin" /> : <FileText size={10} />}
      PDF
    </button>
  );
}

